import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api.js';
import Icon from './Icon.jsx';
import { useMyFlags, flagOn } from '../lib/flags.js';
import { pickFromDrive, OWL_DOC_MIMES } from '../lib/drivePicker.js';
import { useIsMobile } from '../lib/useIsMobile.js';
import { followPageScope } from '../lib/chatScope.js';
import ChartTile from './tiles/ChartTile.jsx';
import ShareMenu from './ShareMenu.jsx';
import JourneyTree, { JourneyFlow, countDecisions } from './JourneyTree.jsx';

// Prompt-starter pills shown on the empty state before /api/owl/starters loads (which
// also returns the user's own most-asked questions). A small mirror so it never opens
// blank; the live list — personalised first — replaces these.
const FALLBACK_STARTERS = [
  { label: "What's new", icon: '✨', prompt: "What's new in Pulse?" },
  { label: "Today's sales", icon: '📊', prompt: 'How are ticket sales going today?' },
  { label: 'Sales overview', icon: '📈', prompt: 'Give me a sales overview' },
  { label: 'Last 7 days', icon: '📅', prompt: 'How have sales gone over the last 7 days?' },
  { label: 'Goal tracking', icon: '🎯', prompt: 'How are my goals tracking?' },
];
// Spark's fallback pills: doing, not data (mirrors the server's Spark defaults;
// label-only — the live server list brings its own icons).
const SPARK_FALLBACK_STARTERS = [
  { label: "What's new", prompt: "What's new in Pulse?" },
  { label: 'Set up an alert', prompt: 'Alert me when ticket sales reach a milestone' },
  { label: 'Goal tracking', prompt: 'How are my goals tracking?' },
  { label: 'Draft a campaign', prompt: 'Draft an email campaign for my next event' },
];

// The native, Claude-powered agentic Owl — the conversational "pull" door onto the
// askData tool (server/owlChat.js). Drops into the same drawer slot as the Inventive
// AnalystDrawer (swapped behind FEATURES.owlNativeChat), mirroring its docked/overlay
// shell so the A/B is apples-to-apples. Answers stream in as plain text; every figure
// is fetched + scoped server-side, so nothing here can reach another client's data.
//
// Mobile-first: single column, full-width panel on phones.
//
// `embed` renders the panel full-bleed with no close/dock chrome — the chromeless
// shell used by the organizer-portal iframe (pages/OwlEmbedPage.jsx, docs/OWL_EMBED.md).
export default function OwlChat({ open, onClose, suiteId, entityId, dashboardId, clients = [], events = [], isAdmin = false, embed = false, seed = null, onSeedUsed }) {
  const isMobile = useIsMobile();
  const [messages, setMessages] = useState([]); // [{ role:'user'|'owl', text }]
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [dock, setDock] = useState(() => localStorage.getItem('howler_owl_dock') || 'docked');
  const [wide, setWide] = useState(() => localStorage.getItem('howler_owl_wide') === '1'); // full-screen the panel
  const [zoom, setZoom] = useState(() => parseFloat(localStorage.getItem('howler_owl_zoom')) || 1);
  // Scope the Owl answers for — pick a client (organiser) and optionally an event.
  const [selEntity, setSelEntity] = useState(entityId || '');
  const [selSuite, setSelSuite] = useState(suiteId || '');
  // Spark or full Owl? The server withholds the data tools when owl.analysis
  // is off for (client, person) — this only re-skins the shell to match: name,
  // starters, no depth toggle, no data attachments. Loading / no client = Owl.
  const myFlags = useMyFlags(selEntity || '');
  const spark = !!myFlags && myFlags['owl.analysis'] === false;
  // Attachments follow the TOOLS, not the Spark/analyst split. Documents
  // (searchDriveDocs / readDriveDoc) ride owl.drive alone, so Spark can read a
  // contract or a picked Drive file perfectly well. Tables ride askUpload, which
  // Spark withholds by design — offering a CSV there would attach a file nothing
  // could read.
  const canDocs = flagOn(myFlags, 'owl.drive');
  const canTables = !spark && flagOn(myFlags, 'owl.uploads');
  const canAttach = canDocs || canTables;
  const aiName = spark ? 'Spark' : 'Owl';
  const [sidebarOpen, setSidebarOpen] = useState(false); // chat list: persistent on desktop, slide-over on mobile
  const [hdrMenuOpen, setHdrMenuOpen] = useState(false); // the header's ⋯ overflow menu (copy/PDF/share/size/dock)
  const [threads, setThreads] = useState([]);
  const [editingId, setEditingId] = useState(null); // thread being renamed inline
  const [editText, setEditText] = useState('');
  const [movingId, setMovingId] = useState(null); // thread whose folder is being changed
  const [confirmDelId, setConfirmDelId] = useState(null); // thread pending a 2nd-tap delete confirm
  const [collapsedFolders, setCollapsedFolders] = useState({});
  const [uploads, setUploads] = useState([]); // attached external data (CSV files / Google Sheets)
  const [driveDocs, setDriveDocs] = useState([]); // Drive documents the Owl can quote (managed on the Drive page)
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachBusy, setAttachBusy] = useState(false);
  const fileRef = useRef(null);
  const [listening, setListening] = useState(false); // voice dictation
  const recogRef = useRef(null);
  const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const toggleMic = () => {
    if (listening) { try { recogRef.current && recogRef.current.stop(); } catch { /* ignore */ } return; }
    if (!SR) return;
    const r = new SR(); recogRef.current = r;
    r.lang = 'en-ZA'; r.interimResults = true; r.continuous = true;
    const base = input ? `${input} ` : '';
    r.onresult = (e) => { let t = ''; for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript; setInput(base + t); };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    try { r.start(); setListening(true); } catch { setListening(false); }
  };
  const [followups, setFollowups] = useState([]); // suggested next questions for the latest answer
  const [persona, setPersona] = useState('quick'); // depth mode: 'quick' (fast) | 'analyst' (deep)
  const [status, setStatus] = useState(''); // live "thinking" label streamed while the Owl works
  const [statusLog, setStatusLog] = useState([]); // the running list of steps this turn — shown ticked-off under the current one
  const pushStatus = (s) => { setStatus(s); setStatusLog((l) => (s && l[l.length - 1] !== s ? [...l, s].slice(-6) : l)); };
  const [commands, setCommands] = useState([]); // "/" slash-command palette (from the tool registry)
  const [starters, setStarters] = useState([]); // empty-state prompt-starter pills (personalised + defaults)
  const [slashIdx, setSlashIdx] = useState(0);   // highlighted command in the palette
  const taRef = useRef(null);                     // the composer textarea (for focus after picking)
  const abortRef = useRef(null);                  // in-flight turn's AbortController (the ⏹ Stop button)
  const [chatCopied, setChatCopied] = useState(false);
  const scrollRef = useRef(null);
  const lastUserRef = useRef(null); // the latest user message, scrolled to the top on a new turn
  // Copy the whole conversation as plain text (Q/Owl transcript) to the clipboard.
  const copyChat = async () => {
    const text = messages.filter((m) => m.text).map((m) => `${m.role === 'user' ? 'Q' : aiName}: ${m.text}`).join('\n\n');
    try { await navigator.clipboard.writeText(text); setChatCopied(true); setTimeout(() => setChatCopied(false), 2000); } catch { /* ignore */ }
  };
  // Save the conversation as a PDF: clone the rendered messages into a print window
  // (charts→PNG, tables/text preserved), seed the app's CSS vars with light values so
  // colours resolve, then trigger the browser's print → "Save as PDF". No dependency.
  const printChat = () => {
    const node = scrollRef.current; if (!node) return;
    const clone = node.cloneNode(true);
    const srcCanvas = node.querySelectorAll('canvas'); const cloneCanvas = clone.querySelectorAll('canvas');
    cloneCanvas.forEach((c, i) => { try { const img = document.createElement('img'); img.src = srcCanvas[i].toDataURL('image/png'); img.style.maxWidth = '100%'; c.replaceWith(img); } catch { /* tainted/none */ } });
    clone.querySelectorAll('button').forEach((b) => b.remove()); // drop interactive controls
    const title = (messages.find((m) => m.role === 'user' && m.text)?.text || `${aiName} chat`).slice(0, 70);
    const scope = [clients.find((c) => c.id === selEntity)?.name, events.find((e) => e.id === selSuite)?.name].filter(Boolean).join(' · ');
    const w = window.open('', '_blank'); if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title}</title><style>:root{--text:#1a1a1a;--muted:#6b7280;--brand:#3b5bfd;--card:#fff;--bg:#fafafe;--hairline:#e2e2e8;--elevated:#f1f1f5}body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#1a1a1a;padding:28px;max-width:780px;margin:0 auto;line-height:1.5}h1{font-size:17px;margin:0 0 2px}.sub{color:#6b7280;font-size:12px;margin:0 0 16px}table{border-collapse:collapse;width:100%;margin:8px 0}th,td{border:1px solid #e2e2e8;padding:5px 9px;text-align:left;font-size:12.5px}img{max-width:100%}</style></head><body><h1>${title}</h1>${scope ? `<p class="sub">${scope}</p>` : ''}${clone.innerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 350);
  };
  const pickDock = (m) => { localStorage.setItem('howler_owl_dock', m); setDock(m); };
  const toggleWide = () => setWide((v) => { const n = !v; localStorage.setItem('howler_owl_wide', n ? '1' : '0'); return n; });
  const bumpZoom = (d) => setZoom((z) => { const n = Math.min(1.3, Math.max(0.8, Math.round((z + d) * 100) / 100)); localStorage.setItem('howler_owl_zoom', String(n)); return n; });

  // Auto-default the event: the first time the picker has data and nothing is in
  // scope, select the client's CURRENT on-sale event that has goals (falling back to
  // any event with goals). Runs once so it never fights a manual change. Only when
  // the picker is visible, so the chosen event is always shown and changeable.
  const autoPicked = useRef(false);
  useEffect(() => {
    if (autoPicked.current || suiteId || selSuite || !events.length) return;
    if (!(isAdmin || clients.length > 1)) return;
    const scope = selEntity ? events.filter((e) => e.entityId === selEntity) : events;
    const withGoals = scope.filter((e) => e.hasGoals);
    const choice = withGoals.find((e) => e.onSale) || withGoals[0];
    if (!choice) return;
    autoPicked.current = true;
    if (!selEntity) setSelEntity(choice.entityId);
    setSelSuite(choice.id);
  }, [events, selEntity, selSuite, suiteId, isAdmin, clients.length]);
  // Changing scope starts a fresh conversation (don't mix clients' data in a thread).
  const resetThread = () => { setMessages([]); setThreadId(null); };

  // Follow the page context if it changes while open — and the rule above holds
  // for a scope change made OUTSIDE the chat (the profile switcher, or navigating
  // onto another event), not only for the header pickers below. It didn't: a thread
  // survived a profile switch with the old event still in its history, and Spark
  // went on answering off that event and called it "the event currently open in
  // your session" (2026-08-21). Nothing is lost — the old chat is saved and still
  // in the chats list. The rules themselves live in lib/chatScope.js.
  const pageScope = useRef({ entityId: entityId || '', suiteId: suiteId || '' });
  useEffect(() => {
    const to = { entityId: entityId || '', suiteId: suiteId || '' };
    const next = followPageScope({ from: pageScope.current, to, selected: { entityId: selEntity, suiteId: selSuite }, live: messages.length > 0 });
    pageScope.current = to;
    if (!next) return;
    setSelEntity(next.entityId); setSelSuite(next.suiteId);
    if (next.reset) resetThread();
  }, [entityId, suiteId]); // eslint-disable-line react-hooks/exhaustive-deps
  const refreshThreads = async () => { try { const r = await api.owlThreads(); setThreads(r.threads || []); } catch { /* ignore */ } };
  const newChat = () => { resetThread(); setInput(''); setEditingId(null); if (isMobile) setSidebarOpen(false); };
  async function loadThread(t) {
    try {
      const r = await api.owlThreadMessages(t.id);
      setMessages(withLastActions(r.messages));
      setThreadId(t.id);
      setSelEntity(t.entityId || ''); setSelSuite(t.suiteId || '');
      setPersona(['analyst', 'operator'].includes(t.persona) ? t.persona : 'quick'); // reflect the chat's saved depth
    } catch { /* ignore */ }
    setEditingId(null);
    if (isMobile) setSidebarOpen(false);
  }
  const startRename = (t) => { setEditingId(t.id); setEditText(t.title || ''); };
  const commitRename = async (id) => {
    const title = editText.trim(); setEditingId(null);
    if (!title) return;
    setThreads((ts) => ts.map((t) => (t.id === id ? { ...t, title } : t)));
    try { await api.owlRenameThread(id, title); } catch { /* ignore */ } refreshThreads();
  };
  // Delete is a reliable in-app two-tap (🗑 → red "Delete?") — no native
  // window.confirm, which can be suppressed in the overlay/PWA and made the
  // trash silently do nothing. Optimistic remove, then reconcile with the server.
  const deleteThread = async (t) => {
    setConfirmDelId(null);
    setThreads((ts) => ts.filter((x) => x.id !== t.id));
    if (t.id === threadId) newChat();
    try { await api.owlDeleteThread(t.id); } catch (e) { window.alert((e && e.message) || 'Could not delete the chat.'); }
    refreshThreads();
  };
  const startMove = (t) => { setEditingId(null); setMovingId(t.id); };
  const setFolder = async (id, folder) => {
    setMovingId(null);
    const f = String(folder || '').trim();
    setThreads((ts) => ts.map((t) => (t.id === id ? { ...t, folder: f } : t)));
    try { await api.owlSetThreadFolder(id, f); } catch { /* ignore */ } refreshThreads();
  };
  // Attached external data (CSV files / live Google Sheets) the Owl can query.
  const attachEntity = selEntity || entityId || '';
  const refreshUploads = () => { if (!attachEntity) { setUploads([]); return; } api.owlUploads(attachEntity).then((r) => setUploads(r.uploads || [])).catch(() => {}); };
  // Drive documents aren't uploads — a Sheet becomes a queryable table, but a
  // Doc/PDF becomes searchable TEXT — so they're listed separately, read-only.
  const refreshDocs = () => {
    if (!attachEntity) { setDriveDocs([]); return; }
    Promise.all([
      api.myDriveSearch(attachEntity, '').then((r) => r.docs || []).catch(() => []),
      api.owlDocs(attachEntity).then((r) => r.docs || []).catch(() => []),
    ]).then(([drive, uploaded]) => setDriveDocs([...drive, ...uploaded]));
  };
  // Attach straight from Drive, mid-conversation: the picker IS the grant, so
  // the Owl can read what's chosen the moment it finishes ingesting. Adding is a
  // Drive-manage action (it changes what the Owl reads for the whole client), so
  // the route enforces the same permission the Drive page does.
  const addFromDrive = async () => {
    if (!attachEntity) return;
    setAttachBusy(true);
    try {
      const docs = await pickFromDrive({ getToken: () => api.myDrivePickerToken(attachEntity), mimeTypes: OWL_DOC_MIMES });
      const failed = [];
      for (const d of docs) {
        try { await api.myDriveAddSource(attachEntity, { fileId: d.id }); } catch (e) { failed.push(`${d.name}: ${e.message}`); }
      }
      if (failed.length) window.alert(`Couldn't add:\n${failed.join('\n')}`);
      if (docs.length > failed.length) { refreshUploads(); refreshDocs(); }
    } catch (e) { window.alert(e.message); }
    setAttachBusy(false);
  };
  // CSV / Excel / PDF from one button. A spreadsheet becomes a queryable TABLE;
  // a PDF becomes searchable TEXT the Owl quotes — different stores, so the file
  // decides where it goes rather than the person having to know.
  const b64 = (file) => new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result).split(',')[1] || ''); r.onerror = reject; r.readAsDataURL(file); });
  const onPickFile = async (e) => {
    const f = e.target.files && e.target.files[0]; if (e.target) e.target.value = '';
    if (!f || !attachEntity) return;
    const base = f.name.replace(/\.[^.]+$/, '');
    const isPdf = /\.pdf$/i.test(f.name) || f.type === 'application/pdf';
    const isXlsx = /\.xlsx?$/i.test(f.name) || /spreadsheetml|ms-excel/.test(f.type || '');
    if (isPdf && !canDocs) { window.alert('This chat reads documents through Google Drive, which is switched off for this client.'); return; }
    if (!isPdf && !canTables) { window.alert('Spreadsheets are queried by the Owl analyst — attach a PDF here, or open the analyst for tables.'); return; }
    setAttachBusy(true);
    try {
      if (isPdf) { await api.owlUploadPdf(attachEntity, base, await b64(f)); refreshDocs(); }
      else if (isXlsx) { const r = await api.owlUploadXlsx(attachEntity, base, await b64(f)); refreshUploads(); if (r.note) window.alert(r.note); }
      else { await api.owlUploadCsv(attachEntity, base, await f.text()); refreshUploads(); }
    } catch (err) { window.alert((err && err.message) || 'Upload failed.'); }
    setAttachBusy(false);
  };
  const addSheet = async () => {
    if (!attachEntity) return;
    const url = window.prompt('Paste a Google Sheet link (it must be shared "anyone with the link", or published to the web).'); if (!url || !url.trim()) return;
    const name = (window.prompt('Name this source', 'Google Sheet') || 'Google Sheet').trim();
    setAttachBusy(true);
    try { await api.owlUploadSheet(attachEntity, name, url.trim()); refreshUploads(); } catch (err) { window.alert((err && err.message) || 'Could not import the sheet.'); }
    setAttachBusy(false);
  };
  const refreshSheet = async (u) => { setAttachBusy(true); try { await api.owlRefreshUpload(u.id); refreshUploads(); } catch (err) { window.alert((err && err.message) || 'Refresh failed.'); } setAttachBusy(false); };
  const removeUpload = async (u) => { if (!window.confirm(`Remove "${u.name}"?`)) return; setUploads((x) => x.filter((z) => z.id !== u.id)); try { await api.owlDeleteUpload(u.id); } catch { /* ignore */ } refreshUploads(); };

  const clientEvents = events.filter((e) => e.entityId === selEntity);
  const showPicker = isAdmin || clients.length > 1;
  // Clients are auto-scoped server-side; admins need a client or event chosen.
  const canAsk = isAdmin ? !!(selEntity || selSuite) : true;

  // On a NEW turn, bring the latest question to the TOP of the view so the answer reads
  // top-down (instead of slamming to the very bottom, behind the composer). Keyed on the
  // number of user turns, so streaming deltas don't keep yanking the scroll.
  const userTurns = messages.reduce((n, m) => n + (m.role === 'user' ? 1 : 0), 0);
  let lastUserIdx = -1; for (let i = messages.length - 1; i >= 0; i--) { if (messages[i].role === 'user') { lastUserIdx = i; break; } }
  useEffect(() => {
    const c = scrollRef.current; const u = lastUserRef.current;
    if (!c) return;
    if (u) c.scrollTop += u.getBoundingClientRect().top - c.getBoundingClientRect().top - 8;
    else c.scrollTop = c.scrollHeight;
  }, [userTurns]); // eslint-disable-line react-hooks/exhaustive-deps
  // Auto-size the composer: 1 row at rest once chatting (more room for the chat), tall on
  // first open (an inviting empty state); grows with content up to a cap.
  const composerFresh = messages.length === 0;
  useEffect(() => { const el = taRef.current; if (!el) return; el.style.height = 'auto'; el.style.height = `${Math.min(200, el.scrollHeight)}px`; }, [input, composerFresh]);
  // Load the saved-chats list when the panel opens; show the sidebar by default on
  // desktop (a persistent column) and hidden on mobile (a slide-over toggled by ☰).
  useEffect(() => { if (open) { refreshThreads(); setSidebarOpen(false); } }, [open, isMobile]); // chats list collapsed by default; toggle with ☰
  // The "/" command palette, sourced from the Owl's tool registry (loads once).
  useEffect(() => { if (open) api.owlCapabilities(selEntity || '').then((r) => setCommands(r.commands || [])).catch(() => {}); }, [open, selEntity]); // per client — Spark's palette has no data commands
  // Prompt-starter pills for the empty state — personalised (the user's most-asked
  // questions for this client) + curated defaults. Re-fetch per client so they're relevant.
  useEffect(() => { if (open) api.owlStarters(selEntity || '').then((r) => setStarters(r.starters || [])).catch(() => {}); }, [open, selEntity]);
  // Load the client's attached data sources (for the 📎 panel + so the Owl can query them).
  useEffect(() => { if (!open || !attachEntity) { setUploads([]); setDriveDocs([]); return; } api.owlUploads(attachEntity).then((r) => setUploads(r.uploads || [])).catch(() => {}); refreshDocs(); }, [open, attachEntity]); // eslint-disable-line react-hooks/exhaustive-deps
  // Seeded build ("Make it happen" on a home suggestion): once open, scope to the
  // suggestion's event and auto-send its goal in OPERATOR mode so the Owl drafts
  // the campaign (+ its audience) for review. Guarded so it fires once per seed;
  // clearing the seed (onSeedUsed) resets the guard for the next build.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!seed) { seededRef.current = false; return; }
    if (!open || seededRef.current) return;
    seededRef.current = true;
    if (seed.suiteId) setSelSuite(seed.suiteId);
    send(seed.prompt, { mode: 'operator', suiteId: seed.suiteId || undefined });
    onSeedUsed && onSeedUsed();
  }, [open, seed]); // eslint-disable-line react-hooks/exhaustive-deps
  // Esc closes (only while open).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  async function send(text, opts = {}) {
    const q = String(text ?? input).trim();
    if (!q || busy) return;
    if (listening) { try { recogRef.current && recogRef.current.stop(); } catch { /* ignore */ } setListening(false); }
    if (!canAsk) { setMessages((m) => [...m, { role: 'owl', text: 'Pick a client (or open an event) above, then ask me — I scope to that organiser.' }]); return; }
    if (text == null) setInput('');
    setFollowups([]);
    const useMode = opts.mode || persona; // a one-off "dig deeper" overrides the toggle for this turn
    setStatusLog([]);
    pushStatus(useMode === 'operator' ? 'Working out the best move…' : useMode === 'analyst' ? 'Digging in…' : 'Thinking…'); // show an immediate indicator before the first token
    // Append the question + an empty Owl bubble we stream into (tagged with the depth used).
    setMessages((m) => [...m, { role: 'user', text: q }, { role: 'owl', text: '', mode: useMode }]);
    setBusy(true);
    const ac = new AbortController(); // powers the ⏹ Stop button
    abortRef.current = ac;
    liveTidRef.current = threadId || null; // refreshed by onThread as soon as the server names the thread
    const appendToOwl = (delta) => setMessages((m) => {
      const next = m.slice();
      for (let i = next.length - 1; i >= 0; i--) { if (next[i].role === 'owl') { next[i] = { ...next[i], text: next[i].text + delta }; break; } }
      return next;
    });
    try {
      const { threadId: tid, sources, followups: fu, actions } = await api.owlChat({ suiteId: (opts.suiteId ?? selSuite) || undefined, entityId: selEntity || undefined, dashboardId: dashboardId || undefined, message: q, threadId, mode: useMode, signal: ac.signal, onThread: (t) => { liveTidRef.current = t; } }, appendToOwl, pushStatus);
      if (tid) { const isNew = tid !== threadId; setThreadId(tid); if (isNew) refreshThreads(); }
      if ((sources && sources.length) || (actions && actions.length)) setMessages((m) => {
        const next = m.slice();
        for (let i = next.length - 1; i >= 0; i--) { if (next[i].role === 'owl') { next[i] = { ...next[i], sources: (sources && sources.length) ? sources : next[i].sources, actions }; break; } }
        return next;
      });
      if (fu && fu.length) setFollowups(fu);
    } catch (e) {
      if (e && (e.name === 'AbortError' || ac.signal.aborted)) appendToOwl('⏹ Stopped.');
      else {
        // The stream died before finishing — phone locked, flaky mobile network, or
        // Safari's raw "Load failed" (the fetch rejected before we even got the thread
        // id). The server keeps working and PERSISTS the answer, so recover it instead
        // of surfacing a fetch error. When we never got the id back, find the just-made
        // thread by its title (= this question) and recover from that.
        setStatus('Connection dropped — still working…');
        let tid = liveTidRef.current || threadId;
        if (!tid) {
          try {
            const r = await api.owlThreads();
            const want = q.trim().slice(0, 80);
            const hit = (r && r.threads ? r.threads : []).find((t) => String(t.title || '').trim() === want);
            tid = hit && hit.id;
          } catch { /* ignore — fall through to the retry note */ }
        }
        if (tid) await recoverAnswer(tid);
        else appendToOwl('⚠ The connection dropped before I could answer that — please tap send to try again.');
      }
    } finally {
      abortRef.current = null;
      setBusy(false);
      setStatus('');
    }
  }
  // Poll the thread until the persisted answer lands (the turn keeps running
  // server-side after a dropped stream), then swap the whole thread in. Bounded:
  // ~2.5 min of polling, then an honest "reopen this chat" note.
  const liveTidRef = useRef(null);
  async function recoverAnswer(tid) {
    setThreadId((cur) => cur || tid);
    for (let i = 0; i < 30; i++) {
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const r = await api.owlThreadMessages(tid);
        const msgs = r.messages || [];
        const last = msgs[msgs.length - 1];
        if (last && last.role !== 'user' && String(last.body || '').trim()) {
          setMessages(withLastActions(msgs));
          refreshThreads();
          return;
        }
      } catch { /* transient — keep polling */ }
    }
    appendLastOwl('📡 The connection dropped and the answer is taking a while — reopen this chat in a minute and it\'ll be here.');
  }
  const appendLastOwl = (delta) => setMessages((m) => {
    const next = m.slice();
    for (let i = next.length - 1; i >= 0; i--) { if (next[i].role === 'owl') { next[i] = { ...next[i], text: (next[i].text ? `${next[i].text}\n\n` : '') + delta }; break; } }
    return next;
  });
  // ⏹ Stop: tell the server to end the turn (a socket close alone no longer
  // stops it — it can't tell "stopped" from "switched screens"), THEN abort.
  const stop = () => {
    const tid = liveTidRef.current || threadId;
    if (tid) api.owlStop(tid).catch(() => {});
    try { abortRef.current && abortRef.current.abort(); } catch { /* already gone */ }
  };
  // "/" command palette: open while the input is just "/word" (no space yet), so it
  // never triggers mid-sentence or on a date like "1/2". Picking a command drops its
  // example question into the box (editable), per the chosen behaviour.
  const slashMatch = /^\/(\w*)$/.exec(input);
  const slashOpen = !!slashMatch && commands.length > 0;
  const slashQuery = slashMatch ? slashMatch[1].toLowerCase() : '';
  const slashMatches = slashOpen ? commands.filter((c) => c.cmd.toLowerCase().includes(slashQuery) || c.label.toLowerCase().includes(slashQuery)) : [];
  const slashSel = slashMatches.length ? Math.min(slashIdx, slashMatches.length - 1) : 0;
  const pickCommand = (c) => { if (!c) return; setInput(c.example); setSlashIdx(0); setTimeout(() => taRef.current && taRef.current.focus(), 0); };
  const openSlash = () => { setInput('/'); setSlashIdx(0); setTimeout(() => taRef.current && taRef.current.focus(), 0); };
  const onKeyDown = (e) => {
    if (slashOpen && slashMatches.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSlashIdx((i) => Math.min(i + 1, slashMatches.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSlashIdx((i) => Math.max(i - 1, 0)); return; }
      if (e.key === 'Enter') { e.preventDefault(); pickCommand(slashMatches[slashSel]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setInput(''); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const docked = dock === 'docked' && !isMobile;
  const hdrBtn = { border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
  // Round "floating" control — chats/more/close sit as soft circular buttons on the
  // header row alongside the scope pickers, so there's no separate scope strip.
  const floatBtn = { border: 'none', background: 'var(--elevated, rgba(128,128,128,0.1))', color: 'var(--text)', cursor: 'pointer', borderRadius: 999, width: 32, height: 32, flexShrink: 0, lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
  const segBtn = (active) => ({ padding: '4px 10px', fontSize: 11.5, fontWeight: 600, border: 'none', borderRadius: 980, cursor: 'pointer', background: active ? 'var(--brand)' : 'transparent', color: active ? '#fff' : 'var(--text)' });
  const menuItem = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', border: 'none', background: 'transparent', color: 'var(--text)', cursor: 'pointer', padding: '8px 10px', borderRadius: 8, fontSize: 13, fontWeight: 600 };
  const selStyle = { padding: '4px 8px', borderRadius: 8, border: 'none', background: 'var(--elevated, rgba(128,128,128,0.08))', color: 'var(--text)', fontSize: 12.5, maxWidth: 200 };

  // The bubble currently being streamed into: the last Owl message while a turn runs.
  const streaming = busy ? (() => { for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === 'owl') return i; return -1; })() : -1;
  const bubble = (m, i) => (
    <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
      <div style={{
        maxWidth: '85%', padding: '8px 12px', borderRadius: 14, fontSize: 14, lineHeight: 1.45, whiteSpace: m.role === 'user' ? 'pre-wrap' : 'normal', wordBreak: 'break-word',
        background: m.role === 'user' ? 'var(--brand)' : 'var(--elevated, rgba(128,128,128,0.12))',
        color: m.role === 'user' ? '#fff' : 'var(--text)',
        borderTopRightRadius: m.role === 'user' ? 4 : 14, borderTopLeftRadius: m.role === 'user' ? 14 : 4,
      }}>{m.role === 'owl'
        ? (m.text
          // Streaming: keep a caret + the live status UNDER the partial answer. Without
          // it every mid-answer pause (a tool round, a long Looker read) looked frozen —
          // the indicator used to vanish the moment the first token landed.
          ? <>
            <OwlMd text={m.text} />
            {streaming === i && <TypingTail label={status} onStop={stop} />}
          </>
          : (busy ? <ThinkingDots label={status} log={statusLog} onStop={stop} /> : ''))
        : m.text}</div>
    </div>
  );

  // Chat list (saved conversations) — a persistent left column on desktop, a slide-over
  // on mobile. Each row loads on tap; inline rename (✎) and delete (🗑) per chat.
  const folders = [...new Set(threads.map((t) => t.folder).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  // Event names offered as ready-made folders (link a chat to an event by name).
  const eventNames = [...new Set((selEntity ? clientEvents : events).map((e) => e.name).filter(Boolean))].filter((n) => !folders.includes(n)).slice(0, 40);
  const rowBtn = { border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontSize: 12, padding: '4px 3px' };
  const inlineFld = { width: '100%', boxSizing: 'border-box', padding: '8px 12px', border: 'none', borderLeft: '2px solid var(--brand)', background: 'var(--card)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', outline: 'none' };
  const renderRow = (t) => {
    const active = t.id === threadId;
    if (editingId === t.id) return (
      <input key={t.id} autoFocus value={editText} onChange={(e) => setEditText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') commitRename(t.id); if (e.key === 'Escape') setEditingId(null); }}
        onBlur={() => commitRename(t.id)} style={inlineFld} />
    );
    if (movingId === t.id) return (
      <select key={t.id} autoFocus defaultValue={t.folder || ''} style={inlineFld}
        onChange={(e) => { const v = e.target.value; if (v === '__new__') { const name = window.prompt('New folder name'); if (name && name.trim()) setFolder(t.id, name.trim()); else setMovingId(null); } else setFolder(t.id, v); }}
        onBlur={() => setMovingId(null)}>
        <option value="">Unfiled</option>
        {folders.length > 0 && <optgroup label="Folders">{folders.map((f) => <option key={f} value={f}>{f}</option>)}</optgroup>}
        {eventNames.length > 0 && <optgroup label="Events">{eventNames.map((n) => <option key={n} value={n}>{n}</option>)}</optgroup>}
        <option value="__new__">＋ New folder…</option>
      </select>
    );
    return (
      <div key={t.id} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--hairline)', background: active ? 'var(--elevated, rgba(128,128,128,0.12))' : 'transparent' }}>
        <button onClick={() => loadThread(t)} style={{ flex: 1, minWidth: 0, textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: '8px 2px 8px 12px', color: 'var(--text)' }}>
          <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title || 'Chat'}</div>
          <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 1 }}>{new Date(t.at).toLocaleString([], { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
        </button>
        <button onClick={() => startMove(t)} title="Move to folder" aria-label="Move to folder" style={rowBtn}>📁</button>
        <button onClick={() => startRename(t)} title="Rename" aria-label="Rename chat" style={rowBtn}>✎</button>
        {confirmDelId === t.id
          ? <button onClick={() => deleteThread(t)} title="Tap again to delete" aria-label="Confirm delete" style={{ ...rowBtn, color: 'var(--error, #dc2626)', fontWeight: 800, paddingRight: 9 }}>Delete?</button>
          : <button onClick={() => { setConfirmDelId(t.id); setTimeout(() => setConfirmDelId((c) => (c === t.id ? null : c)), 3000); }} title="Delete" aria-label="Delete chat" style={{ ...rowBtn, paddingRight: 9 }}>🗑</button>}
      </div>
    );
  };
  const folderHdr = { display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', border: 'none', background: 'var(--elevated, rgba(128,128,128,0.06))', cursor: 'pointer', padding: '6px 12px', fontSize: 11.5, fontWeight: 700, color: 'var(--text)', borderBottom: '1px solid var(--hairline)' };
  const unfiled = threads.filter((t) => !t.folder);
  const sidebarInner = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: isMobile ? '78vw' : 212, maxWidth: isMobile ? 320 : 212, background: 'var(--bg, var(--card))', borderRight: '1px solid var(--hairline)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 10px 9px 12px', borderBottom: '1px solid var(--hairline)', flexShrink: 0 }}>
        <strong style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em', flex: 1 }}>Chats</strong>
        <button onClick={newChat} title="New chat" style={{ border: 'none', background: 'var(--brand)', color: '#fff', borderRadius: 8, padding: '4px 10px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>＋ New</button>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {threads.length === 0 && <div style={{ padding: '12px', fontSize: 12.5, color: 'var(--muted)' }}>No saved chats yet.</div>}
        {folders.map((f) => {
          const items = threads.filter((t) => t.folder === f);
          const collapsed = !!collapsedFolders[f];
          return (
            <div key={f}>
              <button onClick={() => setCollapsedFolders((c) => ({ ...c, [f]: !c[f] }))} style={folderHdr}>
                <span style={{ fontSize: 9 }}>{collapsed ? '▶' : '▼'}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📁 {f}</span>
                <span style={{ color: 'var(--muted)', fontWeight: 500 }}>{items.length}</span>
              </button>
              {!collapsed && items.map(renderRow)}
            </div>
          );
        })}
        {folders.length > 0 && unfiled.length > 0 && <div style={{ ...folderHdr, cursor: 'default', background: 'transparent', color: 'var(--muted)', fontWeight: 600 }}>Unfiled</div>}
        {unfiled.map(renderRow)}
      </div>
    </div>
  );
  const sidebar = sidebarOpen && (isMobile
    ? (
      <div style={{ position: 'absolute', inset: 0, zIndex: 6, display: 'flex' }}>
        <div style={{ boxShadow: '4px 0 20px rgba(0,0,0,0.25)' }}>{sidebarInner}</div>
        <div onClick={() => setSidebarOpen(false)} style={{ flex: 1, background: 'rgba(0,0,0,0.35)' }} />
      </div>
    )
    : <div style={{ flexShrink: 0 }}>{sidebarInner}</div>);

  const panel = (
    // On phones the panel is edge-to-edge (like a native app) — drop the animated
    // gradient frame so it reads borderless, not a card floating in a window.
    <div className={isMobile ? undefined : 'ai-glow'} style={{ height: '100%', width: '100%', background: 'var(--card)', display: 'flex', flexDirection: 'row', overflow: 'hidden', position: 'relative' }}>
      {!isMobile && sidebar}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Single header row: chats ☰, the scope pickers themselves, then ⋯ + close ✕ —
          all as soft floating buttons, so scope lives IN the header (no second row). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 8px', borderBottom: '1px solid var(--hairline)', flexShrink: 0 }}>
        <button onClick={() => setSidebarOpen((o) => !o)} title="Chats" aria-label="Show chats" style={{ ...floatBtn, fontSize: 15 }}>☰</button>
        {showPicker && (clients.length > 1 ? (
          <select value={selEntity} onChange={(e) => { setSelEntity(e.target.value); setSelSuite(''); resetThread(); }} style={{ ...selStyle, padding: '5px 8px', maxWidth: isMobile ? 132 : 200 }} aria-label="Client">
            <option value="">Pick a client…</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        ) : (
          <strong style={{ fontSize: 13, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(clients[0] && clients[0].name) || 'Your data'}</strong>
        ))}
        {showPicker && selEntity && clientEvents.length > 0 && (
          <select value={selSuite} onChange={(e) => { setSelSuite(e.target.value); resetThread(); }} style={{ ...selStyle, padding: '5px 8px', maxWidth: isMobile ? 112 : 200 }} aria-label="Event">
            <option value="">All events</option>
            {clientEvents.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
          </select>
        )}
        <span style={{ flex: 1 }} />
        <div style={{ position: 'relative' }}>
          <button onClick={() => setHdrMenuOpen((o) => !o)} title="More" aria-label="More options" style={{ ...floatBtn, fontSize: 17, fontWeight: 700, ...(hdrMenuOpen ? { background: 'var(--elevated, rgba(128,128,128,0.22))' } : null) }}>⋯</button>
          {hdrMenuOpen && (
            <>
              <div onClick={() => setHdrMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 90 }} />
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 91, background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 12, boxShadow: 'var(--shadow-pop, 0 8px 28px rgba(0,0,0,0.18))', minWidth: 210, padding: 6 }}>
                <button onClick={() => { newChat(); setHdrMenuOpen(false); }} style={menuItem}>✎ New chat</button>
                {!isMobile && !embed && <button onClick={() => { toggleWide(); setHdrMenuOpen(false); }} style={menuItem}>{wide ? '⤡ Exit full screen' : '⤢ Full screen'}</button>}
                <div style={{ height: 1, background: 'var(--hairline)', margin: '5px 4px' }} />
                {messages.some((m) => m.text) && (
                  <>
                    <button onClick={() => { copyChat(); setHdrMenuOpen(false); }} style={menuItem}>{chatCopied ? '✓ Copied' : '📋 Copy chat'}</button>
                    <button onClick={() => { printChat(); setHdrMenuOpen(false); }} style={menuItem}>📄 Save as PDF</button>
                    <div style={{ padding: '2px 4px' }} onClick={() => setHdrMenuOpen(false)}>
                      <ShareMenu
                        heading={`${aiName} chat${messages.find((m) => m.role === 'user' && m.text) ? ' — ' + messages.find((m) => m.role === 'user' && m.text).text.slice(0, 60) : ''}`}
                        text={messages.filter((m) => m.text).map((m) => `${m.role === 'user' ? 'Q' : aiName}: ${m.text}`).join('\n\n')}
                        isMobile={isMobile} variant="tile" title="Share this chat"
                      />
                    </div>
                    <div style={{ height: 1, background: 'var(--hairline)', margin: '5px 4px' }} />
                  </>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px' }}>
                  <span style={{ fontSize: 12.5, color: 'var(--muted)', flex: 1 }}>Text size</span>
                  <button onClick={() => bumpZoom(-0.1)} aria-label="Smaller" style={{ ...hdrBtn, fontSize: 11.5, fontWeight: 700, padding: '4px 8px', border: '1px solid var(--hairline)', borderRadius: 8 }}>A−</button>
                  <button onClick={() => bumpZoom(0.1)} aria-label="Larger" style={{ ...hdrBtn, fontSize: 14.5, fontWeight: 700, padding: '4px 8px', border: '1px solid var(--hairline)', borderRadius: 8 }}>A+</button>
                </div>
                {!isMobile && !embed && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--muted)', flex: 1 }}>Opens as</span>
                    <div style={{ display: 'inline-flex', gap: 2, padding: 2, background: 'var(--elevated, rgba(128,128,128,0.12))', borderRadius: 980 }} title="How the Owl opens">
                      <button onClick={() => pickDock('overlay')} style={segBtn(!docked)}>Overlay</button>
                      <button onClick={() => pickDock('docked')} style={segBtn(docked)}>In-app</button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {!embed && <button onClick={onClose} title="Close" aria-label="Close the Owl" style={{ ...floatBtn, fontSize: 16 }}>✕</button>}
      </div>

      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14, fontSize: `${zoom}em` }}>
        {messages.length === 0 && (
          <div style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.5 }}>
            <p style={{ margin: '4px 0 10px' }}>{spark
              ? "I'm Spark — I get things done in Pulse: alerts, audiences, campaigns, links and answers about Pulse itself. For a deep data dive, open the Owl analyst. Tap a quick start, or ask anything:"
              : 'Ask about your ticket sales in plain English — I pull the answer live from your own data. Tap a quick start, or ask anything:'}</p>
            {/* Prompt-starter pills — the user's most-asked questions first (personalised),
                then curated starters. Tapping one asks it straight away. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '2px 0 4px' }}>
              {(starters.length ? starters : (spark ? SPARK_FALLBACK_STARTERS : FALLBACK_STARTERS)).map((s) => (
                <button key={s.prompt} type="button" onClick={() => send(s.prompt)} disabled={busy || !canAsk}
                  title={s.prompt}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'var(--elevated, rgba(128,128,128,0.08))', color: 'var(--text)', borderRadius: 980, padding: '7px 13px', fontSize: 13, fontWeight: 600, cursor: (busy || !canAsk) ? 'default' : 'pointer', opacity: (busy || !canAsk) ? 0.6 : 1 }}>
                  <span style={{ fontSize: 14 }}>{s.icon}</span>{s.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-owl-msg ref={(m.role === 'user' && i === lastUserIdx) ? lastUserRef : null}>
            {bubble(m, i)}
            {m.role === 'owl' && m.sources && m.sources.length > 0 && <CitationChips sources={m.sources} entityId={selEntity} suiteId={selSuite} canPin={isAdmin} />}
            {m.role === 'owl' && m.actions && m.actions.length > 0 && m.actions.map((a, ai) => <ActionCard key={ai} action={a} suiteId={selSuite} />)}
            {m.role === 'owl' && m.text && !busy && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                <CopyBtn text={m.text} />
                <ShareMenu heading={[...messages.slice(0, i)].reverse().find((x) => x.role === 'user')?.text || `${aiName} answer`} text={m.text} isMobile={isMobile} variant="tile" title="Share this answer" />
                <DataActions source={(m.sources || []).filter((s) => s.kind !== 'dashboard').find((s) => s.rows && s.rows.length)} suiteId={selSuite} entityId={selEntity} />
                {isAdmin && selEntity && <SaveSegmentButton source={(m.sources || []).filter((s) => s.kind !== 'dashboard').find((s) => s.queryBody && s.queryBody.model)} entityId={selEntity} />}
                <ReportToClaude
                  question={[...messages.slice(0, i)].reverse().find((x) => x.role === 'user')?.text || ''}
                  answer={m.text}
                  sources={m.sources}
                  scopeLabel={[clients.find((c) => c.id === selEntity)?.name, events.find((e) => e.id === selSuite)?.name].filter(Boolean).join(' · ')}
                  dashboardId={dashboardId}
                />
                {/* Re-ask this question at Analyst depth (a one-off; doesn't change the
                    toggle). Spark has no deep mode — the analyst brief needs the data tools. */}
                {!spark && (!m.mode || m.mode === 'quick') && (
                  <button onClick={() => { const q = [...messages.slice(0, i)].reverse().find((x) => x.role === 'user')?.text; if (q) send(q, { mode: 'analyst' }); }} title="Re-answer this with deeper analysis" style={msgActionStyle}>🔬 Dig deeper</button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {followups.length > 0 && !busy && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 12px 0', flexShrink: 0 }}>
          {followups.slice(0, 3).map((q, i) => (
            <button key={i} onClick={() => send(q)} style={{ border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', borderRadius: 980, padding: '5px 11px', fontSize: 12.5, cursor: 'pointer' }}>{q}</button>
          ))}
        </div>
      )}
      {attachOpen && canAttach && (
        <div style={{ borderTop: '1px solid var(--hairline)', padding: '8px 12px', flexShrink: 0, background: 'var(--bg, #fafafe)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: uploads.length ? 6 : 0 }}>
            <strong style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.04em', flex: 1 }}>Attached data</strong>
            {canTables && <button onClick={addSheet} disabled={!attachEntity || attachBusy} style={{ border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', borderRadius: 8, padding: '4px 9px', fontSize: 12, cursor: attachEntity ? 'pointer' : 'default' }}>＋ Google Sheet</button>}
            <button onClick={() => fileRef.current && fileRef.current.click()} disabled={!attachEntity || attachBusy} style={{ border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', borderRadius: 8, padding: '4px 9px', fontSize: 12, cursor: attachEntity ? 'pointer' : 'default' }}>⬆ File</button>
            {canDocs && <button onClick={addFromDrive} disabled={!attachEntity || attachBusy} title="Pick a Sheet, Doc, Slides or PDF from your connected Google Drive" style={{ border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', borderRadius: 8, padding: '4px 9px', fontSize: 12, cursor: attachEntity ? 'pointer' : 'default' }}>＋ From Drive</button>}
          </div>
          {!attachEntity && <div style={{ fontSize: 12, color: 'var(--muted)' }}>Pick a client first to attach data.</div>}
          {uploads.map((u) => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12.5 }}>
              <span title={u.source === 'sheet' ? 'Live Google Sheet' : 'Uploaded file'}>{u.source === 'sheet' ? '🔗' : '📄'}</span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text)' }}>{u.name} <span style={{ color: 'var(--muted)' }}>· {u.rowCount} rows</span></span>
              {u.source === 'sheet' && <button onClick={() => refreshSheet(u)} disabled={attachBusy} title="Refresh from the sheet" style={{ border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontSize: 12 }}>↻</button>}
              <button onClick={() => removeUpload(u)} title="Remove" style={{ border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontSize: 12 }}>🗑</button>
            </div>
          ))}
          {driveDocs.map((d) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12.5 }}>
              <Icon name="folder" size={13} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text)' }}>{d.name} <span style={{ color: 'var(--muted)' }}>· {d.source === 'upload' ? 'PDF' : 'Drive document'}</span></span>
              {d.source === 'upload' && <button onClick={async () => { if (!window.confirm(`Remove "${d.name}"?`)) return; try { await api.owlDeleteDoc(d.id); } catch { /* ignore */ } refreshDocs(); }} title="Remove" style={{ border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontSize: 12 }}>✕</button>}
            </div>
          ))}
          {!canTables && <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>Documents only in this chat — spreadsheets are queried by the Owl analyst.</div>}
          {attachEntity && uploads.length === 0 && driveDocs.length === 0 && <div style={{ fontSize: 12, color: 'var(--muted)' }}>No data attached yet. Add a CSV, Excel or PDF file, a Google Sheet, or something from Drive — then ask me about it.</div>}
          {driveDocs.length > 0 && <div style={{ fontSize: 11.5, color: 'var(--muted)', paddingTop: 2 }}>Drive documents are managed on the Drive page; PDFs you upload here stay with this client.</div>}
          <input ref={fileRef} type="file" accept={[canTables ? '.csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : '', canDocs ? '.pdf,application/pdf' : ''].filter(Boolean).join(',')} onChange={onPickFile} style={{ display: 'none' }} />
        </div>
      )}
      {/* Composer, Claude-style: a big text box on top, with attach / mic on the
          left and Send on the right of a row beneath it. */}
      <div style={{ borderTop: '1px solid var(--hairline)', padding: 10, flexShrink: 0 }}>
        {/* Depth toggle: Quick (fast lookups) vs Analyst (deep — multi-cut analysis + a
            recommendation, a little slower). Sets this chat's default; the per-answer
            "🔬 Dig deeper" re-asks one question deep without changing this. Hidden for
            Spark (server pins quick — the deep briefs are askData sweeps). */}
        {!spark && <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 8 }}>
          {[['quick', '⚡ Quick', 'Fast, grounded answers'], ['analyst', '🔬 Analyst', 'Deeper analysis: multiple cuts, the “so what”, and strategic follow-ups (a little slower)'], ['operator', '🧭 Operator', 'Deep analysis AND proactively drafts the best next action (alert / segment / campaign) for you to confirm']].map(([k, lbl, tip]) => (
            <button key={k} type="button" onClick={() => setPersona(k)} title={tip}
              style={{ border: 'none', background: persona === k ? 'var(--brand)' : 'transparent', color: persona === k ? '#fff' : 'var(--muted)', borderRadius: 980, padding: '3px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{lbl}</button>
          ))}
          {persona === 'analyst' && <span style={{ fontSize: 11, color: 'var(--muted)' }}>deeper · a little slower</span>}
          {persona === 'operator' && <span style={{ fontSize: 11, color: 'var(--muted)' }}>deep + proposes an action</span>}
        </div>}
        {/* "/" command palette — a quick menu of what the Owl can answer. */}
        {slashOpen && slashMatches.length > 0 && (
          <div style={{ marginBottom: 6, border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', overflow: 'hidden', boxShadow: '0 6px 22px rgba(0,0,0,0.14)' }}>
            {slashMatches.map((c, i) => (
              <button key={c.cmd} type="button" onMouseEnter={() => setSlashIdx(i)} onClick={() => pickCommand(c)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', border: 'none', borderBottom: i < slashMatches.length - 1 ? '1px solid var(--hairline)' : 'none', background: i === slashSel ? 'var(--elevated, rgba(128,128,128,0.12))' : 'transparent', cursor: 'pointer', padding: '8px 12px', color: 'var(--text)' }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{c.icon}</span>
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>/{c.cmd} · {c.label}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.example}</span>
                </span>
              </button>
            ))}
          </div>
        )}
        <div style={{ border: '1px solid var(--hairline)', borderRadius: 16, background: 'var(--bg, var(--card))', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <textarea
            ref={taRef}
            value={input} onChange={(e) => { setInput(e.target.value); setSlashIdx(0); }} onKeyDown={onKeyDown}
            placeholder={canAsk ? `Ask ${spark ? 'Spark' : 'the Owl'}…  (type / for commands)` : 'Open a client or event to ask'}
            rows={1} disabled={!canAsk}
            style={{ width: '100%', boxSizing: 'border-box', resize: 'none', minHeight: composerFresh ? 108 : 40, maxHeight: 200, overflowY: 'auto', padding: '8px 10px', border: 'none', outline: 'none', background: 'transparent', color: 'var(--text)', fontSize: 14.5, fontFamily: 'inherit', lineHeight: 1.5 }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={openSlash} title="Commands" aria-label="Slash commands" style={{ border: 'none', background: slashOpen ? 'var(--elevated, rgba(128,128,128,0.14))' : 'transparent', color: 'var(--text)', borderRadius: 980, width: 38, height: 38, fontSize: 17, fontWeight: 700, cursor: 'pointer', lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>/</button>
            {canAttach && <button onClick={() => setAttachOpen((o) => !o)} title="Attach a file (CSV, Excel, PDF), a Google Sheet, or something from Drive" aria-label="Attach a file" style={{ border: 'none', background: attachOpen || uploads.length ? 'var(--elevated, rgba(128,128,128,0.14))' : 'transparent', color: 'var(--text)', borderRadius: 980, minWidth: 38, height: 38, padding: '0 11px', fontSize: 16, cursor: 'pointer', lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>📎{uploads.length ? <span style={{ fontSize: 11, fontWeight: 700, marginLeft: 3 }}>{uploads.length}</span> : ''}</button>}
            {SR && <button onClick={toggleMic} title={listening ? 'Stop dictation' : 'Dictate your question'} aria-label="Dictate" style={{ border: 'none', background: listening ? '#e0414a' : 'transparent', color: listening ? '#fff' : 'var(--text)', borderRadius: 980, width: 38, height: 38, fontSize: 16, cursor: 'pointer', lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>🎤</button>}
            <span style={{ flex: 1 }} />
            <button onClick={() => send()} disabled={busy || !input.trim() || !canAsk} aria-label="Send"
              style={{ border: 'none', borderRadius: 980, padding: '0 20px', height: 38, fontSize: 14, fontWeight: 700, cursor: busy || !input.trim() || !canAsk ? 'default' : 'pointer', background: busy || !input.trim() || !canAsk ? 'var(--elevated, rgba(128,128,128,0.18))' : 'var(--brand)', color: busy || !input.trim() || !canAsk ? 'var(--muted)' : '#fff' }}>
              {busy ? '…' : 'Send ↑'}
            </button>
          </div>
        </div>
      </div>
      </div>
      {isMobile && sidebar}
    </div>
  );

  // Embed (organizer-portal iframe): the panel IS the page — full-bleed, no
  // overlay scrim, no dock animation. The host portal owns the surrounding chrome.
  if (embed) return <div style={{ position: 'fixed', inset: 0 }}>{panel}</div>;

  if (docked) {
    const w = wide ? '100vw' : (sidebarOpen && !isMobile ? 'min(800px, 58vw)' : 'min(560px, 44vw)');
    return (
      <div style={{ position: 'relative', flexShrink: 0, height: '100%', width: open ? w : 0, transition: 'width .28s var(--ease-spring, ease)', overflow: 'hidden' }} aria-hidden={!open}>
        <div style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: w }}>{panel}</div>
      </div>
    );
  }

  const w = isMobile ? '100%' : (wide ? '100vw' : (sidebarOpen ? 'min(800px, 96vw)' : 'min(560px, 94vw)'));
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 80, pointerEvents: open ? 'auto' : 'none' }} aria-hidden={!open}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.42)', opacity: open ? 1 : 0, transition: 'opacity .26s ease', backdropFilter: open ? 'blur(2px)' : 'none', WebkitBackdropFilter: open ? 'blur(2px)' : 'none' }} />
      <div style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: w, boxShadow: '-10px 0 30px rgba(0,0,0,0.28)', transform: open ? 'translateX(0)' : 'translateX(100%)', transition: 'transform .26s var(--ease-spring, ease)' }}>{panel}</div>
    </div>
  );
}

// The "thinking" indicator: three bouncing dots + the live status label streamed from
// the server (e.g. "Reading your ticket data…"), so a multi-second tool call never looks
// frozen. Shown in the empty Owl bubble until the first answer token arrives. The ⏹ Stop
// button aborts the turn (closing the stream — the server bails out of the loop too).
// The server's heartbeat only re-sends its status every 10s, so a label sat
// motionless long enough to read as stuck. Both indicators below tick their OWN
// elapsed seconds every second (restarting whenever the label changes), so the
// wait always visibly moves. The server's own "· 12s" stamp is stripped first so
// the two never double up.
function useElapsed(label) {
  const [secs, setSecs] = useState(0);
  const base = String(label || '').replace(/\s*·\s*\d+s\s*$/, '');
  useEffect(() => { setSecs(0); const t = setInterval(() => setSecs((n) => n + 1), 1000); return () => clearInterval(t); }, [base]);
  return { base, secs };
}

// Shown at the END of an answer that is still streaming: a blinking caret plus the
// live status, so "still typing" and "working on the next bit" are never ambiguous.
function TypingTail({ label, onStop }) {
  const { base, secs } = useElapsed(label);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4, color: 'var(--muted)' }}>
      <style>{'@keyframes owl-caret{0%,45%{opacity:1}50%,100%{opacity:0}}'}</style>
      <span aria-hidden="true" style={{ display: 'inline-block', width: 7, height: 14, background: 'currentColor', borderRadius: 1, animation: 'owl-caret 1s step-end infinite', verticalAlign: 'text-bottom' }} />
      <span style={{ fontSize: 12 }}>{base || 'Writing…'}{secs >= 3 ? ` · ${secs}s` : ''}</span>
      {onStop && (
        <button type="button" onClick={onStop} title="Stop this answer"
          style={{ border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', borderRadius: 980, padding: '2px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
          ⏹ Stop
        </button>
      )}
    </span>
  );
}

function ThinkingDots({ label, log, onStop }) {
  // The steps already done this turn tick off above the live one — the user
  // watches the Owl WORK ("Checking your saved audiences ✓ → Drafting…"), not
  // just a pulsing "thinking" state.
  const done = (log || []).slice(0, -1).slice(-3);
  const { base, secs } = useElapsed(label);
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3, color: 'var(--muted)' }}>
      {done.map((s, i) => (
        <span key={i} style={{ fontSize: 12, opacity: 0.65, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: 'var(--brand)', fontWeight: 700 }}>✓</span> {String(s).replace(/…$/, '')}
        </span>
      ))}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <style>{'@keyframes owl-bd{0%,80%,100%{transform:translateY(0);opacity:.35}40%{transform:translateY(-3px);opacity:1}}'}</style>
      <span style={{ display: 'inline-flex', gap: 3 }} aria-hidden="true">
        {[0, 1, 2].map((i) => <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', display: 'inline-block', animation: 'owl-bd 1.2s ease-in-out infinite', animationDelay: `${i * 0.16}s` }} />)}
      </span>
      <span style={{ fontSize: 13 }}>{base || 'Thinking…'}{secs >= 3 ? ` · ${secs}s` : ''}</span>
      {onStop && (
        <button type="button" onClick={onStop} title="Stop this answer"
          style={{ border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', borderRadius: 980, padding: '3px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', minHeight: 26 }}>
          ⏹ Stop
        </button>
      )}
      </span>
    </span>
  );
}

// ── Lightweight markdown for Owl answers: GFM pipe tables, bold/italic/code, and
// bullet lists. (No dep — Pulse has no markdown lib; this covers what the Owl emits.)
function mdInline(text) {
  const out = []; const re = /\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`/g;
  let last = 0, m, key = 0;
  const s = String(text);
  while ((m = re.exec(s))) {
    if (m.index > last) out.push(s.slice(last, m.index));
    if (m[1] != null) out.push(<strong key={key++}>{m[1]}</strong>);
    else if (m[2] != null) out.push(<em key={key++}>{m[2]}</em>);
    else out.push(<code key={key++} style={{ background: 'rgba(128,128,128,0.15)', borderRadius: 4, padding: '0 4px', fontSize: '0.92em' }}>{m[3]}</code>);
    last = re.lastIndex;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}
function splitRow(line) { let s = line.trim(); if (s.startsWith('|')) s = s.slice(1); if (s.endsWith('|')) s = s.slice(0, -1); return s.split('|').map((c) => c.trim()); }
const looksNumeric = (s) => /^[R$€£]?\s?-?[\d,.]+%?$/.test(String(s).trim());
function OwlMd({ text }) {
  const isMobile = useIsMobile();
  const lines = String(text || '').split('\n');
  const blocks = []; let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.includes('|') && i + 1 < lines.length && /-/.test(lines[i + 1]) && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1])) {
      const header = splitRow(line); i += 2; const rows = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) { rows.push(splitRow(lines[i])); i++; }
      blocks.push({ t: 'table', header, rows }); continue;
    }
    if (/^\s*[-*]\s+/.test(line)) { const items = []; while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*]\s+/, '')); i++; } blocks.push({ t: 'ul', items }); continue; }
    if (!line.trim()) { i++; continue; }
    const para = [line]; i++;
    while (i < lines.length && lines[i].trim() && !lines[i].includes('|') && !/^\s*[-*]\s+/.test(lines[i])) { para.push(lines[i]); i++; }
    blocks.push({ t: 'p', text: para.join('\n') });
  }
  const th = { textAlign: 'left', padding: '5px 9px', borderBottom: '1px solid var(--hairline)', color: 'var(--muted)', fontWeight: 600, whiteSpace: 'nowrap' };
  const td = (n) => ({ padding: '5px 9px', borderBottom: '1px solid var(--hairline)', textAlign: n ? 'right' : 'left', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' });
  return (
    <div>
      {blocks.map((b, k) => {
        if (b.t === 'table') {
          // Keep it tabular on EVERY screen. On a phone the table scrolls sideways
          // inside its own container with the first column pinned — instead of
          // exploding each row into a tall stacked card. Momentum + overscroll-contain
          // keep the scroll inside the table rather than dragging the page/chat.
          const stickyL = { position: 'sticky', left: 0, zIndex: 1, borderRight: '1px solid var(--hairline)' };
          // Download THIS table (as written in the answer) — markdown stripped to plain text.
          const plain = (s) => String(s ?? '').replace(/\*\*|__|\*|_|`/g, '').trim();
          const tableCsv = () => downloadText(
            `${plain(b.header[0] || 'table').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'owl-table'}.csv`,
            [b.header.map((h) => csvEscape(plain(h))).join(','), ...b.rows.map((r) => r.map((c) => csvEscape(plain(c))).join(','))].join('\n'),
          );
          return (
            <div key={k} style={{ margin: '6px 0' }}>
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y', overscrollBehavior: 'contain', border: '1px solid var(--hairline)', borderRadius: 10 }}>
                <table style={{ borderCollapse: 'collapse', fontSize: isMobile ? '0.85em' : '0.92em', width: '100%', minWidth: isMobile ? 'max-content' : undefined }}>
                  <thead><tr>{b.header.map((h, j) => <th key={j} style={j === 0 ? { ...th, ...stickyL, zIndex: 2, background: 'var(--elevated, #f1f1f5)' } : th}>{mdInline(h)}</th>)}</tr></thead>
                  <tbody>{b.rows.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci} style={ci === 0 ? { ...td(false), ...stickyL, background: 'var(--card)', fontWeight: 600 } : td(ci > 0 && looksNumeric(c))}>{mdInline(c)}</td>)}</tr>)}</tbody>
                </table>
              </div>
              <div style={{ textAlign: 'right', marginTop: 2 }}>
                <button type="button" onClick={tableCsv} title="Download this table as CSV" style={{ border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontSize: 10.5, fontWeight: 600, padding: '1px 4px' }}>⬇ Table CSV</button>
              </div>
            </div>
          );
        }
        if (b.t === 'ul') return <ul key={k} style={{ margin: '4px 0', paddingLeft: 18 }}>{b.items.map((it, j) => <li key={j} style={{ margin: '1px 0' }}>{mdInline(it)}</li>)}</ul>;
        return <p key={k} style={{ margin: '4px 0', whiteSpace: 'pre-wrap' }}>{mdInline(b.text)}</p>;
      })}
    </div>
  );
}

// Format a measure value with thousands separators (numbers) or pass strings through.
function fmtVal(v) {
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) && String(v).trim() !== '' ? n.toLocaleString() : String(v);
}

// Format a result-table cell: numbers get thousands separators; dates/strings pass
// through (a YYYY-MM-DD has inner dashes so it won't be mistaken for a number).
function fmtCell(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'number') return v.toLocaleString();
  const s = String(v);
  return /^-?\d+(\.\d+)?$/.test(s) ? Number(s).toLocaleString() : s;
}

// ── CSV export ────────────────────────────────────────────────────────────────
// A source carries the result table (columns + rows) — export exactly what's shown.
function csvEscape(v) { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; }
function toCSV(columns, rows) {
  const head = (columns || []).map((c) => csvEscape(c.label)).join(',');
  const body = (rows || []).map((r) => (columns || []).map((c) => csvEscape(r[c.field])).join(',')).join('\n');
  return `${head}\n${body}`;
}
function downloadText(filename, text, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
const csvName = (source) => `${[source.measure, ...(source.dimensions || [])].filter(Boolean).join(' by ') || 'owl-data'}`
  .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) + '.csv';
// ECharts paints to a transparent canvas → flatten onto white before JPEG (else black).
function downloadCanvasJpg(canvas, filename) {
  try {
    const tmp = document.createElement('canvas'); tmp.width = canvas.width; tmp.height = canvas.height;
    const ctx = tmp.getContext('2d'); ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, tmp.width, tmp.height); ctx.drawImage(canvas, 0, 0);
    const a = document.createElement('a'); a.href = tmp.toDataURL('image/jpeg', 0.92); a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  } catch { /* tainted */ }
}

// The result rows as a data table (columns + values). Reused by the chart's "Table"
// view; measure columns are right-aligned.
function SourceTable({ source }) {
  const cols = source.columns || [];
  const rows = source.rows || [];
  return (
    <div style={{ overflow: 'auto', maxHeight: 260, border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11.5 }}>
        <thead><tr>{cols.map((c, k) => <th key={k} style={{ textAlign: c.kind === 'measure' ? 'right' : 'left', padding: '6px 10px', position: 'sticky', top: 0, background: 'var(--elevated, #f1f1f5)', color: 'var(--muted)', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: '1px solid var(--hairline)' }}>{c.label}</th>)}</tr></thead>
        <tbody>{rows.map((r, ri) => <tr key={ri}>{cols.map((c, k) => <td key={k} style={{ padding: '5px 10px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--hairline)', fontVariantNumeric: 'tabular-nums', textAlign: c.kind === 'measure' ? 'right' : 'left', color: 'var(--text)' }}>{fmtCell(r[c.field])}</td>)}</tr>)}</tbody>
      </table>
      {source.count > rows.length && <div style={{ padding: '6px 10px', fontSize: 10.5, color: 'var(--muted)' }}>Showing {rows.length} of {source.count.toLocaleString()} rows.</div>}
    </div>
  );
}

// Map an Owl citation source into the Looker-shaped data ChartTile renders. Bars
// show the biggest first (rows arrive measure-desc); line charts re-sort by the
// date dimension so time runs left→right.
const VIS = { line: 'looker_line', bar: 'looker_column', pie: 'looker_pie' };

// Two dimensions + one measure (e.g. check-ins by GATE and HOUR) pivot into one
// SERIES per value of the lower-cardinality dimension — so gates compare side by
// side (and can Stack) instead of flattening both dims onto one long axis.
function pivotInfo(s) {
  const dims = (s.columns || []).filter((c) => c.kind === 'dimension');
  const meas = (s.columns || []).filter((c) => c.kind === 'measure');
  if (dims.length !== 2 || meas.length !== 1) return null;
  const rows = s.rows || [];
  if (rows.length < 3) return null;
  const d0 = new Set(rows.map((r) => String(r[dims[0].field]))).size;
  const d1 = new Set(rows.map((r) => String(r[dims[1].field]))).size;
  const [sCol, xCol] = d0 <= d1 ? [dims[0], dims[1]] : [dims[1], dims[0]];
  const sN = Math.min(d0, d1); const xN = Math.max(d0, d1);
  if (sN < 2 || sN > 12 || xN < 2) return null; // a legend of 13+ series is unreadable
  return { sCol, xCol, mCol: meas[0], seriesVals: [...new Set(rows.map((r) => String(r[sCol.field])))] };
}
const timeLike = (v) => /^\d{4}-\d{2}/.test(v) || /^\d{1,2}:\d{2}/.test(v) || /^\d+$/.test(v);
function chartDataFromSource(s) {
  const p = pivotInfo(s);
  if (p) {
    const { sCol, xCol, mCol, seriesVals } = p;
    const byX = new Map();
    for (const r of (s.rows || [])) { const x = String(r[xCol.field]); if (!byX.has(x)) byX.set(x, {}); byX.get(x)[String(r[sCol.field])] = r[mCol.field]; }
    let xs = [...byX.keys()];
    const sumX = (x) => Object.values(byX.get(x)).reduce((t, v) => t + (Number(v) || 0), 0);
    // Time-ish x (dates, hours) runs left→right; categorical x sorts biggest-first.
    if (s.chartType === 'line' || xs.every(timeLike)) xs.sort((a, b) => a.localeCompare(b));
    else { xs.sort((a, b) => sumX(b) - sumX(a)); xs = xs.slice(0, 15); }
    const key = (v) => `${mCol.field}|${v}`;
    return {
      fields: {
        dimensions: [{ name: xCol.field, label: xCol.label, label_short: xCol.label }],
        measures: seriesVals.map((v) => ({ name: key(v), label: v, label_short: v })),
      },
      data: xs.map((x) => { const o = { [xCol.field]: { value: x } }; const m = byX.get(x); for (const v of seriesVals) o[key(v)] = { value: m[v] != null ? m[v] : null }; return o; }),
    };
  }
  const dims = s.columns.filter((c) => c.kind === 'dimension');
  const meas = s.columns.filter((c) => c.kind === 'measure');
  let rows = s.rows || [];
  if (s.chartType === 'line' && dims[0]) rows = [...rows].sort((a, b) => String(a[dims[0].field]).localeCompare(String(b[dims[0].field])));
  else rows = rows.slice(0, 15); // top 15 categories keeps bars readable
  return {
    fields: {
      dimensions: dims.map((c) => ({ name: c.field, label: c.label, label_short: c.label })),
      measures: meas.map((c) => ({ name: c.field, label: c.label, label_short: c.label })),
    },
    data: rows.map((r) => { const o = {}; for (const c of s.columns) o[c.field] = { value: r[c.field] }; return o; }),
  };
}

// Compile a structured "fix brief" from an Owl answer — the question, the answer, and
// the EXACT query behind it (measures, group-bys, filters, scope, dashboard) — so the
// user can hand it to Claude verbatim instead of writing one by hand or screenshotting.
function buildFixBrief({ question, answer, sources, scopeLabel, dashboardId }) {
  const lines = [];
  lines.push('FIX BRIEF — Owl answer');
  if (scopeLabel) lines.push(`Scope: ${scopeLabel}`);
  if (dashboardId) lines.push(`Dashboard: ${dashboardId}`);
  lines.push('');
  lines.push(`Question: ${question || '(unknown)'}`);
  lines.push('');
  lines.push('Owl answered:');
  lines.push(String(answer || '').trim() || '(no text)');
  const dataSrc = (sources || []).filter((s) => s.kind !== 'dashboard');
  const dashSrc = (sources || []).filter((s) => s.kind === 'dashboard');
  if (dataSrc.length) {
    lines.push('');
    lines.push('Underlying query the Owl ran:');
    dataSrc.forEach((s, n) => {
      const qb = s.queryBody || {};
      const measures = (qb.fields || []).filter((f) => (s.columns || []).some((c) => c.field === f && c.kind === 'measure'));
      const groupBy = (s.dimensions || []);
      const filters = (s.filters || []).map((f) => `${f.label}=${f.value}`).join(', ');
      lines.push(`${dataSrc.length > 1 ? `[${n + 1}] ` : ''}explore=${qb.model || ''}/${qb.view || s.explore || ''}`);
      lines.push(`  measures: ${measures.join(', ') || s.measure || '(none)'}`);
      lines.push(`  group by: ${groupBy.join(', ') || '(none)'}`);
      lines.push(`  filters: ${filters || '(scope only)'}`);
      lines.push(`  rows: ${s.count != null ? s.count : (s.rows || []).length}`);
      lines.push(`  query: ${JSON.stringify(qb)}`);
    });
  }
  // getDashboard answers: list each tile the Owl read + the explore/fields/filters behind it.
  dashSrc.forEach((s) => {
    lines.push('');
    lines.push(`Dashboard read: ${s.dashboard?.title || ''} (${s.dashboard?.id || ''})`);
    (s.tiles || []).forEach((ti) => {
      const filters = (ti.filters || []).map((f) => `${f.label}=${f.value}`).join(', ');
      lines.push(`- ${ti.title}: value=${ti.value != null ? ti.value : '(none)'} | explore=${ti.explore || '(?)'} | fields=[${(ti.fields || []).join(', ')}]${filters ? ` | filters=${filters}` : ''}`);
    });
  });
  lines.push('');
  lines.push("What's wrong / what it should be: (describe here)");
  return lines.join('\n');
}

// Small text-action button styling shared by the per-message actions.
const msgActionStyle = { border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontSize: 11.5, padding: '2px 4px', display: 'inline-flex', alignItems: 'center', gap: 4 };
// 🎯 "Save as segment" — turn ANY chat answer's cohort into a reusable audience
// ("from any point of a chat"). Reuses the answer's live query (its filters define the
// cohort); the server re-applies scope + only saves from the ticket-data explore. No PII.
function SaveSegmentButton({ source, entityId }) {
  const [state, setState] = useState(''); // '' | 'busy' | 'done'
  if (!source || !source.queryBody || !source.queryBody.model) return null;
  const save = async () => {
    const suggested = [source.measure, ...(source.dimensions || [])].filter(Boolean).join(' by ').slice(0, 60) || 'Owl segment';
    const name = window.prompt('Name this segment (a reusable audience of these people):', `${suggested} audience`.slice(0, 80));
    if (name == null) return;
    setState('busy');
    const qb = source.queryBody;
    try {
      await api.owlCreateSegment({ entityId, name: name.trim(), draft: { mode: 'query', model: qb.model, view: qb.view, queryFilters: qb.filters || {} } });
      setState('done'); setTimeout(() => setState(''), 2500);
    } catch (e) { setState(''); window.alert((e && e.message) || 'Could not save the segment.'); }
  };
  return (
    <button onClick={save} disabled={state === 'busy'} title="Save these people as a reusable audience (Engage → Segments)" style={msgActionStyle}>
      {state === 'done' ? '✓ Saved' : (state === 'busy' ? '🎯 Saving…' : '🎯 Save as segment')}
    </button>
  );
}
// Copy a single message's text to the clipboard.
function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  return (
    <button onClick={async () => { try { await navigator.clipboard.writeText(String(text || '')); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* ignore */ } }}
      title="Copy this answer" style={msgActionStyle}>{done ? '✓ Copied' : '📋 Copy'}</button>
  );
}

// CSV + chart-image downloads for an answer, sitting inline with the other message
// actions. The image grabs the answer's rendered chart canvas (found via the message
// wrapper's data attribute), so no ref-threading into the chart is needed.
// CSV = the RAW query data: when the citation preview is capped (50 rows) the button
// re-runs the query live server-side (same scope gates) and downloads ALL rows.
function DataActions({ source, suiteId, entityId }) {
  const [busy, setBusy] = useState(false);
  if (!source || !(source.rows && source.rows.length)) return null;
  const hasChart = !!source.chartType;
  const capped = (source.count || 0) > source.rows.length && !!source.queryBody;
  const downloadCsv = async () => {
    if (busy) return;
    let rows = source.rows;
    if (capped) {
      setBusy(true);
      try { const r = await api.owlExportRows({ queryBody: source.queryBody, suiteId: suiteId || undefined, entityId: entityId || undefined }); if (r && Array.isArray(r.rows) && r.rows.length) rows = r.rows; }
      catch { /* fall back to the preview rows rather than failing the download */ }
      setBusy(false);
    }
    downloadText(csvName(source), toCSV(source.columns, rows));
  };
  return (
    <>
      <button onClick={downloadCsv} disabled={busy} title={capped ? `Download ALL ${Number(source.count).toLocaleString()} rows as CSV (re-runs the query live)` : 'Download the data as CSV (opens in Excel/Sheets)'} style={msgActionStyle}>{busy ? '⬇ Fetching…' : `⬇ CSV${capped ? ` (${Number(source.count).toLocaleString()})` : ''}`}</button>
      {hasChart && <button onClick={(e) => { const c = e.currentTarget.closest('[data-owl-msg]') && e.currentTarget.closest('[data-owl-msg]').querySelector('canvas'); if (c) downloadCanvasJpg(c, csvName(source).replace(/\.csv$/, '.jpg')); }} title="Download the chart as an image (JPEG)" style={msgActionStyle}>⬇ Image</button>}
    </>
  );
}

// ── Action card: the act-layer's confirm step ──────────────────────────────────
// An act-tool (createAlert) DRAFTS something; nothing is created until the user taps
// here. This is the safe draft→confirm pattern the riskier act-tools will reuse. The
// commit re-checks permission server-side (alerts.manage), so this button can never
// create something the user couldn't make by hand.
const OP_TEXT = { gte: 'reaches', lte: 'drops to', gt: 'goes above', lt: 'drops below' };
// "View it" deep-link shown after an action is created — takes the user to the page
// where the new alert / segment / campaign lives (the commit route returns the path).
function ViewLink({ url, label = 'View it →' }) {
  if (!url) return null;
  return <a href={url} style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 700, textDecoration: 'none', marginLeft: 8 }}>{label}</a>;
}
// Dispatch to the right confirm card by action kind (act-tools share the pattern).
// Stored thread → chat bubbles. The draft cards ("Create segment", "Create alert"…)
// come back only on a FRESH final answer — the one a dropped stream or a quick
// reopen would otherwise strand without its button. Older drafts stay text-only so
// reopening history can't invite a second create of something already confirmed.
const ACTIONS_FRESH_MS = 30 * 60 * 1000;
function withLastActions(msgs) {
  const list = msgs || [];
  const last = list[list.length - 1];
  const fresh = last && last.role !== 'user' && last.actions && last.actions.length && Date.now() - (Date.parse(last.at) || 0) < ACTIONS_FRESH_MS;
  return list.map((m, i) => ({ role: m.role === 'user' ? 'user' : 'owl', text: m.body, sources: m.sources, ...(fresh && i === list.length - 1 ? { actions: m.actions } : {}) }));
}

function ActionCard({ action, suiteId }) {
  if (!action) return null;
  if (action.kind === 'createAlert') return <AlertActionCard action={action} />;
  if (action.kind === 'createGoal') return <GoalActionCard action={action} />;
  if (action.kind === 'createLiveUpdate') return <LivePulseActionCard action={action} />;
  if (action.kind === 'createSegment') return <SegmentActionCard action={action} />;
  if (action.kind === 'createLookalike') return <LookalikeActionCard action={action} />;
  if (action.kind === 'draftCampaign') return <CampaignActionCard action={action} suiteId={suiteId} />;
  if (action.kind === 'draftJourney') return <JourneyActionCard action={action} suiteId={suiteId} />;
  if (action.kind === 'rememberFact') return <MemoryActionCard action={action} />;
  if (action.kind === 'draftReport') return <ReportActionCard action={action} />;
  if (action.kind === 'createChottuLink') return <ChottuLinkActionCard action={action} />;
  if (action.kind === 'applyChottuTemplate') return <ChottuTemplateActionCard action={action} />;
  if (action.kind === 'adsManage') return <AdsManageActionCard action={action} />;
  if (action.kind === 'adsCreate') return <AdsCreateActionCard action={action} />;
  if (action.kind === 'adsCreative') return <AdsCreativeActionCard action={action} />;
  return null;
}

// A CREATIVE VARIANT: another ad inside an ad set that already exists. Short
// card on purpose — budget, audience and schedule belong to the ad set and
// aren't being changed, so listing them would imply they were.
function AdsCreativeActionCard({ action }) {
  const [state, setState] = useState('');
  const [err, setErr] = useState('');
  const d = action.draft || {};
  const apply = async () => {
    setState('busy'); setErr('');
    try { await api.owlAdsCreative({ entityId: action.entityId, draft: d }); setState('done'); }
    catch (e) { setState('error'); setErr((e && e.message) || 'Meta did not accept the creative.'); }
  };
  return (
    <div style={{ margin: '2px 0 10px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '10px 12px', maxWidth: '85%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <strong style={{ fontSize: 12.5 }}>New creative — A/B test</strong>
        <span style={{ fontSize: 11, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>Draft</span>
      </div>
      <div style={{ fontSize: 13, marginBottom: 6 }}>
        Add a <strong>{d.format || 'image'}</strong> creative to <strong>{d.adsetName}</strong>
        {d.campaignName ? <span style={{ color: 'var(--muted)' }}> (in {d.campaignName})</span> : null}.
      </div>
      {d.imagePreview && <img src={d.imagePreview} alt="" style={{ width: '100%', maxWidth: 260, borderRadius: 10, marginBottom: 6, display: 'block' }} />}
      {d.message && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 6, whiteSpace: 'pre-wrap' }}>{d.message}{d.headline ? `\n— ${d.headline}` : ''}</div>}
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 8 }}>
        It runs against what's already in that ad set on the same budget and audience, and starts PAUSED — resume it from the ad set to begin the test.
      </div>
      {state === 'done'
        ? <div style={{ fontSize: 12.5, fontWeight: 600 }}>✓ Added, paused. Turn it on from the ad set when you're ready.</div>
        : <button onClick={apply} disabled={state === 'busy'} style={{ padding: '8px 14px', borderRadius: 980, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 40 }}>{state === 'busy' ? 'Adding…' : 'Add paused creative'}</button>}
      {err && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

// New Meta ad, end to end — the card spells out every setting because tapping it
// creates real campaign objects (always PAUSED; activating is a separate step).
function AdsCreateActionCard({ action }) {
  const [state, setState] = useState(''); // '' | 'busy' | 'done' | 'error'
  const [err, setErr] = useState('');
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState(null); // null | 'busy' | { html, format }
  const d = action.draft || {};
  const money = (v) => `${d.currency ? `${d.currency} ` : ''}${v}`;
  const FORMAT_LABEL = { image: 'Image', video: 'Video (serves in Reels/Stories too)', carousel: `Carousel (${(d.cards || []).length} cards)`, boost: 'Boost a published post' };
  const rows = [
    ['Format', FORMAT_LABEL[d.format || 'image']],
    ['Ad account', d.account], ['Page', d.page],
    ['Objective', d.objective === 'sales' ? 'Sales (pixel purchases)' : 'Traffic (link clicks)'],
    ['Daily budget', money(d.dailyBudget)],
    ['Placements', d.platforms === 'facebook' ? 'Facebook only' : d.platforms === 'instagram' ? 'Instagram only' : 'Facebook + Instagram (automatic)'],
    ['Targeting', d.audienceName || d.lookalikeName ? `Audience: ${[d.lookalikeName, d.audienceName].filter(Boolean).join(' + ')}` : `${(d.countries || []).join(', ')}${d.ageMin || d.ageMax ? ` · ages ${d.ageMin || 13}–${d.ageMax || 65}` : ''}`],
    ...(d.detailedName ? [['Interests', d.detailedName]] : []),
    ...(d.detailedExcludeName ? [['Not interested in', d.detailedExcludeName]] : []),
    ...(d.excludeName ? [['Excluding', `${d.excludeName} — they won't see it`]] : []),
    ...(d.lookalikeName ? [['New audience', `${d.lookalikeName} — Meta builds it when you confirm`]] : []),
    ...(d.eventName ? [['Event', d.eventName]] : []),
    ...(d.targetPurchases ? [['Sales target', `${d.targetPurchases} purchases (Meta-attributed)`]] : []),
    ['Goes live', d.launchName || 'When you resume it from the Manage column — it is created paused'],
    ...(d.format === 'video' && d.videoNote ? [['Video', d.videoNote]] : []),
    ...(d.format === 'boost' && d.boostNote ? [['Post', d.boostNote]] : []),
    ...(d.format !== 'boost' ? [['Link', d.linkFromEvent ? `${d.link} — the ticket link from ${d.linkFromEvent}` : d.link], ['CTA', d.cta]] : []),
    ...(d.endDate ? [['Ends', d.endDate]] : []),
  ];
  const runPreview = async (pf) => {
    setPreview('busy');
    try { setPreview(await api.adsCreatePreview(action.entityId, { ...d, adFormat: pf })); }
    catch (e) { setPreview(null); setErr(`Preview: ${(e && e.message) || 'failed'}`); }
  };
  const create = async () => {
    setState('busy'); setErr('');
    try {
      const r = await api.owlAdsCreate({ entityId: action.entityId, draft: d });
      setUrl((r && r.url) || ''); setState('done');
    } catch (e) { setState('error'); setErr((e && e.message) || 'Meta did not accept the create.'); }
  };
  return (
    <div style={{ margin: '2px 0 10px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '10px 12px', maxWidth: '85%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <strong style={{ fontSize: 12.5 }}>New Meta ad</strong>
        <span style={{ fontSize: 11, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>Draft</span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{d.name}</div>
      <div style={{ fontSize: 12, marginBottom: 6 }}>
        {rows.map(([k, v]) => <div key={k} style={{ display: 'flex', gap: 8, marginBottom: 2 }}><span style={{ color: 'var(--muted)', minWidth: 84 }}>{k}</span><span style={{ overflowWrap: 'anywhere' }}>{v}</span></div>)}
      </div>
      {d.message && <div style={{ fontSize: 12, background: 'rgba(128,128,128,0.08)', borderRadius: 8, padding: '6px 9px', marginBottom: 6, whiteSpace: 'pre-wrap' }}>{d.headline ? `${d.headline}\n` : ''}{d.message}</div>}
      {(d.imagePreview || d.imageUrl) ? (
        <div style={{ marginBottom: 6 }}>
          <img src={d.imagePreview || d.imageUrl} alt="" style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 8, display: 'block' }} />
          {d.imageNote && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>Image {d.imageNote}</div>}
        </div>
      ) : (
        <div style={{ fontSize: 11.5, color: '#b45309', marginBottom: 6 }}>No image chosen — Meta will use the link's preview image. Ask for one ("use the image from the BLACKOUT post") for a stronger ad.</div>
      )}
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 8 }}>Created <b>paused</b> — it cannot spend until you resume it from the Paid-ads Manage column.</div>
      {state === 'done' ? (
        <div style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 600 }}>✓ Created on Meta (paused). Review it, then resume it to go live.<ViewLink url={url} label="View paid ads →" /></div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
            {[['MOBILE_FEED_STANDARD', 'FB feed'], ['INSTAGRAM_STANDARD', 'IG feed'], ['INSTAGRAM_REELS', 'Reels']].map(([k, l]) => (
              <button key={k} onClick={() => runPreview(k)} disabled={preview === 'busy'}
                style={{ padding: '4px 10px', borderRadius: 980, border: '1px solid var(--hairline)', background: preview && preview !== 'busy' && preview.format === k ? 'var(--brand)' : 'var(--card)', color: preview && preview !== 'busy' && preview.format === k ? '#fff' : 'var(--muted)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                {preview === 'busy' ? '…' : `Preview: ${l}`}
              </button>
            ))}
          </div>
          {preview && preview !== 'busy' && (
            <div style={{ border: '1px solid var(--hairline)', borderRadius: 10, overflow: 'auto', marginBottom: 8, display: 'flex', justifyContent: 'center' }} dangerouslySetInnerHTML={{ __html: preview.html }} />
          )}
          <button onClick={create} disabled={state === 'busy'}
            style={{ padding: '7px 14px', borderRadius: 980, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
            {state === 'busy' ? 'Creating…' : 'Create paused ad'}
          </button>
          {state === 'error' && <div style={{ fontSize: 12, color: 'var(--error, #d70015)', marginTop: 6 }}>{err}</div>}
        </>
      )}
    </div>
  );
}

// Live Meta-ads change (pause/resume/budget) — the ONE card that touches real
// ad spend, so it spells out current → new and what tapping it does.
function AdsManageActionCard({ action }) {
  const [state, setState] = useState(''); // '' | 'busy' | 'done' | 'error'
  const [err, setErr] = useState('');
  const [url, setUrl] = useState('');
  const d = action.draft || {};
  const money = (v) => (v == null ? '—' : `${d.currency ? `${d.currency} ` : ''}${v}`);
  // An ad-set change has to SAY it's an ad set — "pause Lookalike 1%" and
  // "pause the campaign" look identical otherwise, and mean very different spend.
  const what = <><strong>{d.name}</strong>{d.level === 'ad set' ? <> <span style={{ color: 'var(--muted)' }}>(ad set in {d.campaignName})</span></> : null}</>;
  const line = d.action === 'pause' ? <>Pause {what} (currently {d.status || 'ACTIVE'}).</>
    : d.action === 'resume' ? <>Resume {what} (currently {d.status || 'PAUSED'}).</>
    : <>Set {what} {d.action === 'set_daily_budget' ? 'daily' : 'lifetime'} budget to <strong>{money(d.amount)}</strong> (now {money(d.action === 'set_daily_budget' ? d.dailyBudget : d.lifetimeBudget)}).</>;
  const apply = async () => {
    setState('busy'); setErr('');
    try {
      const r = await api.owlAdsManage({ entityId: action.entityId, draft: d });
      setUrl((r && r.url) || ''); setState('done');
    } catch (e) { setState('error'); setErr((e && e.message) || 'Meta did not accept the change.'); }
  };
  return (
    <div style={{ margin: '2px 0 10px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '10px 12px', maxWidth: '85%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <strong style={{ fontSize: 12.5 }}>Meta ads change</strong>
        <span style={{ fontSize: 11, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>Draft</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 4 }}>{line}</div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 8 }}>{d.account ? `Ad account: ${d.account} · ` : ''}Changes live ad delivery/spend on Meta — audit-logged.</div>
      {state === 'done' ? (
        <div style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 600 }}>✓ Applied on Meta.<ViewLink url={url} label="View paid ads →" /></div>
      ) : (
        <>
          <button onClick={apply} disabled={state === 'busy'}
            style={{ padding: '7px 14px', borderRadius: 980, border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
            {state === 'busy' ? 'Applying…' : d.action === 'pause' ? 'Pause campaign' : d.action === 'resume' ? 'Resume campaign' : 'Change budget'}
          </button>
          {state === 'error' && <div style={{ fontSize: 12, color: 'var(--error, #d70015)', marginTop: 6 }}>{err}</div>}
        </>
      )}
    </div>
  );
}

// "Create draft journey" — the Owl authored a branching journey (draftJourney
// tool); the decision tree renders right in the chat and tapping creates a DRAFT
// sequence campaign in Engage → Campaigns (permission re-checked server-side by
// the create route). The user finishes audience + copy there; nothing sends.
function JourneyActionCard({ action, suiteId }) {
  const [state, setState] = useState('');
  const [err, setErr] = useState('');
  const [savedSeg, setSavedSeg] = useState(null);
  const decisions = countDecisions(action.nodes);
  const updating = !!action.updatesActionId; // amends an existing draft in place
  // Broadcast the draft so the Engage → Journeys page (the live canvas) renders
  // the tree full-size in the main body while the chat stays the assistant.
  // Rendering an older thread re-broadcasts its journey — last card wins. The
  // page can also ARRIVE LATE (user navigates to Journeys after the card
  // rendered) — it asks via howler:journey-request and we re-broadcast.
  useEffect(() => {
    const send = () => { try { window.dispatchEvent(new CustomEvent('howler:journey-draft', { detail: action })); } catch { /* ignore */ } };
    send();
    window.addEventListener('howler:journey-request', send);
    return () => window.removeEventListener('howler:journey-request', send);
  }, [action]);
  const reach = action.reach || null;
  const reachLine = reach ? `${fmtVal(reach.total)} people${reach.email != null ? ` · ${fmtVal(reach.email)} emailable` : ''}${reach.sms ? ` · ${fmtVal(reach.sms)} SMS` : ''}` : null;
  const create = async () => {
    setState('busy'); setErr('');
    // The server saves a new chat cohort as a reusable segment first, then creates
    // the draft sequence campaign — same auto-save behaviour as draftCampaign.
    try {
      const r = await api.owlDraftJourney({ entityId: action.entityId, suiteId: suiteId || undefined, name: action.name, goal: action.goal, summary: action.summary, nodes: action.nodes, audience: action.audience, audienceName: action.audienceName, master: action.master || undefined, updatesActionId: action.updatesActionId || undefined });
      setSavedSeg(r?.segment || null); setState('done');
      // Tell the Journeys page (if open) the draft is real — it refreshes its list.
      try { window.dispatchEvent(new CustomEvent('howler:journey-created', { detail: { id: r?.journey?.id } })); } catch { /* ignore */ }
    } catch (e) { setState('error'); setErr((e && e.message) || 'Could not create the draft journey.'); }
  };
  return (
    <div style={{ margin: '2px 0 10px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '10px 12px', maxWidth: '95%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <span style={{ fontSize: 15 }}>🧭</span>
        <strong style={{ fontSize: 12.5 }}>Journey</strong>
        <span style={{ fontSize: 11, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>{updating ? 'Update' : 'Draft'}</span>
        {decisions > 0 && <span style={{ fontSize: 11, color: '#b45309', fontWeight: 700 }}>◆ {decisions} decision{decisions === 1 ? '' : 's'}</span>}
      </div>
      <div style={{ fontSize: 13, marginBottom: 2 }}><strong>{action.name}</strong>{action.master ? <span style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 400 }}> · in “{action.master}”</span> : null}</div>
      {/* An amend REPLACES a draft rather than adding another one — say which, and
          say that its audience is untouched unless this card carries a new one. */}
      {updating && (
        <div style={{ fontSize: 12, color: '#b45309', marginBottom: 3 }}>
          {action.updatesLive
            ? <>Changes your <strong>live</strong> journey “{action.updatesTitle || action.name}” — it takes effect on the next run; people already partway through keep their place, pick up new steps only when they reach them, and nobody is re-sent anything.</>
            : <>Replaces your existing draft “{action.updatesTitle || action.name}”</>}
          {action.audience ? '' : ' Its audience stays as it is.'}
        </div>
      )}
      {action.audienceName && <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 2 }}>→ “{action.audienceName}”{reachLine ? ` · ${reachLine}` : ''}</div>}
      {action.summary && <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.45, marginBottom: 6 }}>{action.summary}</div>}
      <JourneyTree nodes={action.nodes} />
      <JourneyFlow nodes={action.nodes} compact />
      {/* The user told the Owl to keep steps that land on/after the event's start
          day (the tool refuses them otherwise) — say which, right above the button,
          so the tap is made knowing it. Same amber as the wait-mismatch flag. */}
      {action.eventTiming?.late?.length > 0 && (
        <div style={{ margin: '6px 0 8px', fontSize: 11.5, fontWeight: 700, color: '#b45309', background: 'rgba(245,158,11,0.18)', borderRadius: 8, padding: '5px 8px', lineHeight: 1.4 }}
          title="Counted from the moment the journey starts, along the same clock as the Flow summary. The event day comes from the briefing's Event start date.">
          ⚠ Sends after {action.eventTiming.name || 'the event'} starts ({action.eventTiming.start}) — you said that's OK:
          <ul style={{ margin: '3px 0 0', paddingLeft: 16, fontWeight: 500 }}>
            {action.eventTiming.late.map((l) => <li key={l.id}>“{l.subject}”{l.branch ? ` (${l.branch})` : ''} — {l.when}</li>)}
          </ul>
        </div>
      )}
      {state === 'done' ? (
        <div style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 600 }}>
          ✓ {updating ? 'Journey updated' : 'Draft created'}{savedSeg ? <> — audience saved as segment “{savedSeg.name}”<ViewLink url="/engage/segments" label="Segments →" /></> : (updating ? ' — the draft now has these steps; approve it in Campaigns when you are happy.' : ' — finish the audience and approve it in Campaigns.')}
          <ViewLink url="/engage/campaigns" label="Open Campaigns →" />
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={create} disabled={state === 'busy'} style={{ border: 'none', background: 'var(--brand)', color: '#fff', borderRadius: 9, padding: '7px 13px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', opacity: state === 'busy' ? 0.6 : 1 }}>
            {state === 'busy' ? (updating ? 'Updating…' : 'Creating…') : (updating ? 'Update journey' : 'Create draft journey')}
          </button>
          <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{updating ? (action.updatesLive ? 'Applies to the running journey from its next tick — nobody is re-sent anything.' : 'Rewrites that draft in Campaigns — still a draft, nothing sends until you approve it.') : 'Lands as a draft in Campaigns — you review and approve before anything sends.'}</span>
          {state === 'error' && <span style={{ fontSize: 12, color: 'var(--error, #d33)' }}>{err}</span>}
        </div>
      )}
    </div>
  );
}

// "Create link" — the Owl drafted one ChottuLink deep link; tapping mints it on
// ChottuLink (permission re-checked server-side) and shows the live short URL.
function ChottuLinkActionCard({ action }) {
  const [state, setState] = useState('');
  const [err, setErr] = useState('');
  const [made, setMade] = useState(null);
  const d = action.draft || {};
  const utmLine = Object.entries(d.utm || {}).map(([k, v]) => `${k}=${v}`).join(' · ');
  const create = async () => {
    setState('busy'); setErr('');
    try { const r = await api.owlCreateChottuLink({ entityId: action.entityId, suiteId: action.suiteId, draft: d }); setMade(r.link); setState('done'); }
    catch (e) { setState('error'); setErr((e && e.message) || 'Could not create the link.'); }
  };
  return (
    <div style={{ margin: '2px 0 10px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '10px 12px', maxWidth: '85%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <span style={{ fontSize: 15 }}>🔗</span>
        <strong style={{ fontSize: 12.5 }}>Deep link</strong>
        <span style={{ fontSize: 11, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>Draft</span>
      </div>
      <div style={{ fontSize: 13, marginBottom: 4 }}><strong>{d.linkName}</strong></div>
      <div style={{ fontSize: 12, color: 'var(--brand)', marginBottom: 4, wordBreak: 'break-all' }}>{action.domain}/{d.path || '(auto code)'}</div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: utmLine ? 2 : 8, wordBreak: 'break-all' }}>→ {d.destinationUrl}</div>
      {utmLine && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 8 }}>{utmLine}</div>}
      {state === 'done' ? (
        <div style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 600 }}>
          ✓ Link is live: <span style={{ userSelect: 'all' }}>{made?.shortUrl}</span>
          <button onClick={() => { try { navigator.clipboard.writeText(made?.shortUrl || ''); } catch { /* older browsers */ } }} style={{ marginLeft: 8, border: '1px solid var(--hairline)', background: 'none', borderRadius: 8, padding: '2px 8px', fontSize: 11.5, cursor: 'pointer', color: 'var(--text)' }}>Copy</button>
          <ViewLink url={ '/engage/links' } label="View links →" />
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={create} disabled={state === 'busy'}
            style={{ border: 'none', borderRadius: 980, padding: '6px 16px', fontSize: 12.5, fontWeight: 700, cursor: state === 'busy' ? 'default' : 'pointer', background: state === 'busy' ? 'var(--elevated, rgba(128,128,128,0.18))' : 'var(--brand)', color: state === 'busy' ? 'var(--muted)' : '#fff' }}>
            {state === 'busy' ? 'Creating…' : 'Create link'}
          </button>
          {state === 'error' && <span style={{ fontSize: 12, color: '#e0414a' }}>{err}</span>}
        </div>
      )}
    </div>
  );
}

// "Create N links" — the Owl resolved a link template against the current event;
// tapping creates the whole set sequentially and reports each ✓/✗.
function ChottuTemplateActionCard({ action }) {
  const [state, setState] = useState('');
  const [err, setErr] = useState('');
  const [outcome, setOutcome] = useState(null);
  const items = action.items || [];
  const create = async () => {
    setState('busy'); setErr('');
    try {
      const r = await api.owlApplyChottuTemplate({ entityId: action.entityId, suiteId: action.suiteId, templateId: action.templateId, base: action.base, items: items.map((i) => ({ key: i.key })) });
      setOutcome(r); setState('done');
    } catch (e) { setState('error'); setErr((e && e.message) || 'Could not create the links.'); }
  };
  return (
    <div style={{ margin: '2px 0 10px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '10px 12px', maxWidth: '85%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <span style={{ fontSize: 15 }}>⚡</span>
        <strong style={{ fontSize: 12.5 }}>Link set — {action.templateName}</strong>
        <span style={{ fontSize: 11, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>Draft</span>
      </div>
      {state !== 'done' && (
        <div style={{ fontSize: 12, marginBottom: 8 }}>
          {items.map((i) => (
            <div key={i.key} style={{ padding: '3px 0' }}>
              <span style={{ color: 'var(--brand)' }}>…/{i.path || '(auto)'}</span> <span style={{ color: 'var(--muted)' }}>· {i.name}</span>
              {(i.warnings || []).map((w, k) => <div key={k} style={{ fontSize: 11.5, color: '#b25000' }}>⚠ {w}</div>)}
            </div>
          ))}
        </div>
      )}
      {state === 'done' ? (
        <div style={{ fontSize: 12.5 }}>
          <div style={{ color: 'var(--brand)', fontWeight: 700, marginBottom: 4 }}>
            {outcome.failed ? `✓ ${outcome.created} created · ✗ ${outcome.failed} failed` : `✓ All ${outcome.created} links created`}
            <ViewLink url={outcome.url || '/engage/links'} label="View links →" />
          </div>
          {(outcome.results || []).map((r) => (
            <div key={r.key} style={{ padding: '2px 0', color: r.ok ? 'var(--text)' : '#e0414a' }}>
              {r.ok ? <>✓ <span style={{ userSelect: 'all' }}>{r.link.shortUrl}</span></> : <>✗ {r.key}: {r.error}</>}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={create} disabled={state === 'busy'}
            style={{ border: 'none', borderRadius: 980, padding: '6px 16px', fontSize: 12.5, fontWeight: 700, cursor: state === 'busy' ? 'default' : 'pointer', background: state === 'busy' ? 'var(--elevated, rgba(128,128,128,0.18))' : 'var(--brand)', color: state === 'busy' ? 'var(--muted)' : '#fff' }}>
            {state === 'busy' ? 'Creating…' : `Create ${items.length} link${items.length === 1 ? '' : 's'}`}
          </button>
          {state === 'error' && <span style={{ fontSize: 12, color: '#e0414a' }}>{err}</span>}
        </div>
      )}
    </div>
  );
}
// "File it" — the Owl drafted a product report (bug/idea) from the chat; tapping
// files it to the product board (same path as the report widget), tagged source=owl.
const REPORT_ICON = { bug: '🐞', improvement: '✨', idea: '💡' };
function ReportActionCard({ action }) {
  const [state, setState] = useState(''); // '' | 'busy' | 'done' | 'error'
  const [err, setErr] = useState('');
  const [url, setUrl] = useState('');
  const d = action.draft || {};
  const file = async () => {
    setState('busy'); setErr('');
    try { const r = await api.owlSubmitReport({ draft: d }); setUrl((r && r.url) || '/product'); setState('done'); }
    catch (e) { setState('error'); setErr((e && e.message) || 'Could not file the report.'); }
  };
  return (
    <div style={{ margin: '2px 0 10px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '10px 12px', maxWidth: '85%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <span style={{ fontSize: 15 }}>{REPORT_ICON[d.type] || '🐞'}</span>
        <strong style={{ fontSize: 12.5 }}>Report</strong>
        <span style={{ fontSize: 11, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px', textTransform: 'capitalize' }}>{d.type || 'bug'}{d.type === 'bug' && d.urgency && d.urgency !== 'normal' ? ` · ${d.urgency}` : ''}</span>
      </div>
      {d.title && <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{d.title}</div>}
      {d.description && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{d.description}</div>}
      {state === 'done' ? (
        <div style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 600 }}>✓ Filed — the team can see it. <ViewLink url={url} label="Track it →" /></div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={file} disabled={state === 'busy'} style={{ border: 'none', borderRadius: 980, padding: '6px 16px', fontSize: 12.5, fontWeight: 700, cursor: state === 'busy' ? 'default' : 'pointer', background: state === 'busy' ? 'var(--elevated, rgba(128,128,128,0.18))' : 'var(--brand)', color: state === 'busy' ? 'var(--muted)' : '#fff' }}>{state === 'busy' ? 'Filing…' : '📮 File it'}</button>
          {state === 'error' && <span style={{ fontSize: 12, color: '#e0414a' }}>{err}</span>}
        </div>
      )}
    </div>
  );
}
// "Remember this?" — the Owl proposes a durable client fact; tapping saves it to memory.
function MemoryActionCard({ action }) {
  const [state, setState] = useState(''); // '' | 'busy' | 'done' | 'error'
  const [err, setErr] = useState('');
  const save = async () => {
    setState('busy'); setErr('');
    try { await api.owlRemember({ entityId: action.entityId, suiteId: action.suiteId, fact: action.fact, scope: action.memScope }); setState('done'); }
    catch (e) { setState('error'); setErr((e && e.message) || 'Could not save that.'); }
  };
  return (
    <div style={{ margin: '2px 0 10px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '10px 12px', maxWidth: '85%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <span style={{ fontSize: 15 }}>🧠</span>
        <strong style={{ fontSize: 12.5 }}>Remember</strong>
        <span style={{ fontSize: 11, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>{action.memScope === 'event' ? 'This event' : action.memScope === 'user' ? 'Just you' : 'Client'}</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 8 }}>“{action.fact}”</div>
      {state === 'done' ? (
        <div style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 600 }}>✓ Saved — I’ll remember that {action.memScope === 'event' ? 'for this event' : action.memScope === 'user' ? 'just for you' : 'for this client'}.</div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={save} disabled={state === 'busy'} style={{ border: 'none', borderRadius: 980, padding: '6px 16px', fontSize: 12.5, fontWeight: 700, cursor: state === 'busy' ? 'default' : 'pointer', background: state === 'busy' ? 'var(--elevated, rgba(128,128,128,0.18))' : 'var(--brand)', color: state === 'busy' ? 'var(--muted)' : '#fff' }}>{state === 'busy' ? 'Saving…' : '🧠 Remember it'}</button>
          {state === 'error' && <span style={{ fontSize: 12, color: '#e0414a' }}>{err}</span>}
        </div>
      )}
    </div>
  );
}
function AlertActionCard({ action }) {
  const [state, setState] = useState(''); // '' | 'busy' | 'done' | 'error'
  const [err, setErr] = useState('');
  const [suiteId, setSuiteId] = useState(action.suiteId || ''); // chosen event (when none was selected)
  const [url, setUrl] = useState(''); // where to view it once created
  const d = action.draft || {};
  const cond = `${d.metricLabel || d.measureLabel || 'this metric'} ${OP_TEXT[d.operator] || 'reaches'} ${fmtVal(d.threshold)}${d.unit === '%' ? '%' : ''}`;
  const CHAN_LABEL = { push: 'push', email: 'email', sms: 'SMS', slack: 'Slack' };
  const chans = (d.channels || []).map((c) => CHAN_LABEL[c] || c);
  const delivery = `via ${['in-app', ...chans].join(', ')}${d.priority === 'important' ? ' · important' : ''}`;
  const events = action.events || [];
  const create = async () => {
    if (!suiteId) return; // need an event first
    setState('busy'); setErr('');
    try {
      const r = await api.owlCreateAlert({ suiteId, draft: d });
      setUrl((r && r.url) || '');
      setState('done');
    } catch (e) { setState('error'); setErr((e && e.message) || 'Could not create the alert.'); }
  };
  return (
    <div style={{ margin: '2px 0 10px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '10px 12px', maxWidth: '85%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <span style={{ fontSize: 15 }}>🔔</span>
        <strong style={{ fontSize: 12.5 }}>Alert</strong>
        <span style={{ fontSize: 11, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>Draft</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 4 }}>Notify me when <strong>{cond}</strong>.</div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 8 }}>{delivery}</div>
      {action.needsEvent && state !== 'done' && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 3 }}>Which event should this watch?</div>
          <select value={suiteId} onChange={(e) => setSuiteId(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 8, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 13 }}>
            <option value="">Pick an event…</option>
            {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
          </select>
        </div>
      )}
      {state === 'done' ? (
        <div style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 600 }}>✓ Alert created — you'll be notified when it triggers.<ViewLink url={url} label="View alerts →" /></div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={create} disabled={state === 'busy' || !suiteId}
            style={{ border: 'none', borderRadius: 980, padding: '6px 16px', fontSize: 12.5, fontWeight: 700, cursor: (state === 'busy' || !suiteId) ? 'default' : 'pointer', background: (state === 'busy' || !suiteId) ? 'var(--elevated, rgba(128,128,128,0.18))' : 'var(--brand)', color: (state === 'busy' || !suiteId) ? 'var(--muted)' : '#fff' }}>
            {state === 'busy' ? 'Creating…' : 'Create alert'}
          </button>
          {action.needsEvent && !suiteId && <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>Pick an event above</span>}
          {state === 'error' && <span style={{ fontSize: 12, color: '#e0414a' }}>{err}</span>}
        </div>
      )}
    </div>
  );
}

// Goal confirm card — a target on a number, tracked live with pace + forecast.
// Same draft→confirm shape as the alert card; the commit re-checks goals.manage
// server-side, so the Owl can only create what this person could create by hand.
function GoalActionCard({ action }) {
  const [state, setState] = useState(''); // '' | 'busy' | 'done' | 'error'
  const [err, setErr] = useState('');
  const [suiteId, setSuiteId] = useState(action.suiteId || '');
  const [url, setUrl] = useState('');
  const d = action.draft || {};
  const ref = d.metricRef || {};
  const events = action.events || [];
  const aim = d.direction === 'at_most' ? 'Keep' : 'Reach';
  const rel = d.direction === 'at_most' ? 'under' : '';
  const create = async () => {
    if (!suiteId) return;
    setState('busy'); setErr('');
    try {
      const r = await api.owlCreateGoal({ suiteId, draft: d });
      setUrl((r && r.url) || '/goals');
      setState('done');
    } catch (e) { setState('error'); setErr((e && e.message) || 'Could not create the goal.'); }
  };
  return (
    <div style={{ margin: '2px 0 10px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '10px 12px', maxWidth: '85%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <Icon name="goals" size={14} />
        <strong style={{ fontSize: 12.5 }}>Goal</strong>
        <span style={{ fontSize: 11, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>Draft</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 4 }}>
        {aim} <strong>{ref.metricLabel || ref.measureLabel || 'this metric'}</strong> {rel && `${rel} `}
        <strong>{fmtVal(d.targetValue)}{d.unit === '%' ? '%' : ''}</strong>{d.byDate ? <> by <strong>{d.byDate}</strong></> : ''}.
      </div>
      {/* The baseline the Owl looked up for a relative ask ("up 20% on last year").
          The number is saved with the goal (it drives "vs last time"); the note is
          just provenance for this card, so you can sanity-check the maths here. */}
      {d.baselineValue ? (
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
          vs last time <strong style={{ color: 'var(--text)' }}>{fmtVal(d.baselineValue)}</strong>
          {d.baselineNote ? ` · ${d.baselineNote}` : ''}
        </div>
      ) : null}
      {/* Last time's curve, computed from the past event (no spreadsheet) — it's
          what makes the pace line follow how sales really built up last time. */}
      {action.curve ? (
        <div style={{ fontSize: 11.5, color: action.curve.error ? '#b45309' : 'var(--muted)', marginBottom: 4 }}>
          {action.curve.error
            ? `Couldn't compute last time's curve (${action.curve.error}) — the goal will pace on a straight line.`
            : `Paced against ${action.curve.name} · ${action.curve.points} days of sales computed from your data`}
        </div>
      ) : null}
      {/* Which event the live number counts. Pinned by name server-side, so the goal
          reads the same event its "vs last time" curve is measured against. */}
      {action.countsEvent ? (
        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 4 }}>Counts <strong style={{ color: 'var(--text)' }}>{action.countsEvent}</strong> only</div>
      ) : (!action.needsEvent && (
        <div style={{ fontSize: 11.5, color: '#b45309', marginBottom: 4 }}>This event has no event lock set, so the number counts everything in scope — ask Howler to set the event on it if it looks wrong.</div>
      ))}
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 8 }}>
        {d.byDate ? 'Tracked live with pace and a forecast' : 'Tracked live — add a deadline later for pace and a forecast'}{d.tag ? ` · ${d.tag}` : ''}
      </div>
      {action.needsEvent && state !== 'done' && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 3 }}>Which event is this goal for?</div>
          <select value={suiteId} onChange={(e) => setSuiteId(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 8, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 13 }}>
            <option value="">Pick an event…</option>
            {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
          </select>
        </div>
      )}
      {state === 'done' ? (
        <div style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 600 }}>✓ Goal created — it starts tracking straight away.<ViewLink url={url} label="View goals →" /></div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={create} disabled={state === 'busy' || !suiteId}
            style={{ border: 'none', borderRadius: 980, padding: '6px 16px', fontSize: 12.5, fontWeight: 700, cursor: (state === 'busy' || !suiteId) ? 'default' : 'pointer', background: (state === 'busy' || !suiteId) ? 'var(--elevated, rgba(128,128,128,0.18))' : 'var(--brand)', color: (state === 'busy' || !suiteId) ? 'var(--muted)' : '#fff' }}>
            {state === 'busy' ? 'Creating…' : 'Create goal'}
          </button>
          {action.needsEvent && !suiteId && <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>Pick an event above</span>}
          {state === 'error' && <span style={{ fontSize: 12, color: '#e0414a' }}>{err}</span>}
        </div>
      )}
    </div>
  );
}

// Live update confirm card — sets up a recurring event-day multi-metric snapshot
// (the Alerts page's "Live updates" tab). Same draft→confirm shape as the alert
// card; commit re-checks alerts.manage server-side. The user still presses Go live
// on event day (or sets a window in the editor) — nothing sends until then.
function LivePulseActionCard({ action }) {
  const [state, setState] = useState(''); // '' | 'busy' | 'done' | 'error'
  const [err, setErr] = useState('');
  const [suiteId, setSuiteId] = useState(action.suiteId || '');
  const [url, setUrl] = useState('');
  const d = action.draft || {};
  const CHAN_LABEL = { push: 'push', email: 'email', sms: 'SMS', whatsapp: 'WhatsApp' };
  const chans = (d.channels || []).map((c) => CHAN_LABEL[c] || c);
  const events = action.events || [];
  const create = async () => {
    if (!suiteId) return;
    setState('busy'); setErr('');
    try { const r = await api.owlCreateLiveUpdate({ suiteId, draft: d }); setUrl((r && r.url) || ''); setState('done'); }
    catch (e) { setState('error'); setErr((e && e.message) || 'Could not set up the live update.'); }
  };
  return (
    <div style={{ margin: '2px 0 10px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '10px 12px', maxWidth: '85%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <span style={{ fontSize: 15 }}>⚡</span>
        <strong style={{ fontSize: 12.5 }}>Live update</strong>
        <span style={{ fontSize: 11, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>Draft</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 4 }}>
        Every <strong>{d.cadenceMin || 30} min</strong> while the event is live: <strong>{(d.blocks || []).map((b) => b.label || (b.type === 'eventops' ? 'Devices' : 'Metric')).join(' · ')}</strong>.
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 8 }}>via {['in-app', ...chans].join(', ')} · press Go live on event day (Alerts → Live updates)</div>
      {action.needsEvent && state !== 'done' && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 3 }}>Which event is this for?</div>
          <select value={suiteId} onChange={(e) => setSuiteId(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 8, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 13 }}>
            <option value="">Pick an event…</option>
            {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
          </select>
        </div>
      )}
      {state === 'done' ? (
        <div style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 600 }}>✓ Live update set up — press Go live on event day.<ViewLink url={url} label="View live updates →" /></div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={create} disabled={state === 'busy' || !suiteId}
            style={{ border: 'none', borderRadius: 980, padding: '6px 16px', fontSize: 12.5, fontWeight: 700, cursor: (state === 'busy' || !suiteId) ? 'default' : 'pointer', background: (state === 'busy' || !suiteId) ? 'var(--elevated, rgba(128,128,128,0.18))' : 'var(--brand)', color: (state === 'busy' || !suiteId) ? 'var(--muted)' : '#fff' }}>
            {state === 'busy' ? 'Setting up…' : '⚡ Set it up'}
          </button>
          {action.needsEvent && !suiteId && <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>Pick an event above</span>}
          {state === 'error' && <span style={{ fontSize: 12, color: '#e0414a' }}>{err}</span>}
        </div>
      )}
    </div>
  );
}

// Segment confirm card — saves a reusable audience from a chat cohort. PII-safe: shows
// only the count + per-channel reach, never people. Commit re-checks campaigns.approve.
function SegmentActionCard({ action }) {
  const [state, setState] = useState('');
  const [err, setErr] = useState('');
  const [url, setUrl] = useState('');
  const reach = action.reach || null;
  const reachLine = reach
    ? `${fmtVal(reach.total)} people${reach.email != null ? ` · ${fmtVal(reach.email)} emailable` : ''}${reach.sms ? ` · ${fmtVal(reach.sms)} SMS` : ''}`
    : null;
  const create = async () => {
    setState('busy'); setErr('');
    try { const r = await api.owlCreateSegment({ entityId: action.entityId, name: action.name, draft: action.draft }); setUrl((r && r.url) || ''); setState('done'); }
    catch (e) { setState('error'); setErr((e && e.message) || 'Could not create the segment.'); }
  };
  return (
    <div style={{ margin: '2px 0 10px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '10px 12px', maxWidth: '85%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <span style={{ fontSize: 15 }}>🎯</span>
        <strong style={{ fontSize: 12.5 }}>Segment</strong>
        <span style={{ fontSize: 11, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>Draft</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: reachLine ? 4 : 8 }}>Save <strong>{action.summary || action.name}</strong> as a reusable audience.</div>
      {reachLine && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: action.scopeLabel ? 2 : 8 }}>{reachLine}</div>}
      {/* WHICH events the count came from. A cohort scoped to the open event reads
          far smaller than the client expects ("8k? we have 15k+"), so the card says
          it out loud instead of leaving the number unexplained. */}
      {action.scopeLabel && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="ticket" size={12} />{action.scopeLabel}</div>}
      {state === 'done' ? (
        <div style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 600 }}>✓ Segment saved — find it in Engage → Segments.<ViewLink url={url} label="View segments →" /></div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={create} disabled={state === 'busy'}
            style={{ border: 'none', borderRadius: 980, padding: '6px 16px', fontSize: 12.5, fontWeight: 700, cursor: state === 'busy' ? 'default' : 'pointer', background: state === 'busy' ? 'var(--elevated, rgba(128,128,128,0.18))' : 'var(--brand)', color: state === 'busy' ? 'var(--muted)' : '#fff' }}>
            {state === 'busy' ? 'Saving…' : 'Create segment'}
          </button>
          {state === 'error' && <span style={{ fontSize: 12, color: '#e0414a' }}>{err}</span>}
        </div>
      )}
    </div>
  );
}

// Lookalike confirm card — Meta goes and finds NEW people resembling an audience
// the client already holds. Nothing is built until the tap; the commit route
// re-checks permission and both flags. No ad is involved, which is the whole point
// of it living here — the old path made you start an ad to get one.
function LookalikeActionCard({ action }) {
  const [state, setState] = useState('');
  const [err, setErr] = useState('');
  const [url, setUrl] = useState('');
  const [done, setDone] = useState(null);
  const build = async () => {
    setState('busy'); setErr('');
    try {
      const r = await api.owlCreateLookalike({ entityId: action.entityId, segmentId: action.segmentId, ratio: action.ratio, country: action.country });
      setUrl((r && r.url) || ''); setDone(r); setState('done');
    } catch (e) { setState('error'); setErr((e && e.message) || 'Could not build the lookalike.'); }
  };
  return (
    <div style={{ margin: '2px 0 10px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '10px 12px', maxWidth: '85%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <Icon name="segments" size={15} />
        <strong style={{ fontSize: 12.5 }}>Lookalike audience</strong>
        <span style={{ fontSize: 11, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>Draft</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 4 }}>Build a <strong>{action.percent}% lookalike</strong> of “{action.seed}” in {action.country}.</div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 8 }}>Meta finds new people who resemble that audience. {action.percent <= 2 ? 'Closest resemblance, smallest reach.' : 'Broader reach, looser resemblance.'} It takes a few hours to populate, and it is targetable in any ad — no ad is created here.</div>
      {state === 'done' ? (
        <div style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 600 }}>✓ {done && done.existed ? 'That lookalike already existed — reusing it.' : 'Lookalike building on Meta — give it a few hours to fill.'}<ViewLink url={url} label="View audiences →" /></div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={build} disabled={state === 'busy'}
            style={{ border: 'none', borderRadius: 980, padding: '6px 16px', fontSize: 12.5, fontWeight: 700, cursor: state === 'busy' ? 'default' : 'pointer', background: state === 'busy' ? 'var(--elevated, rgba(128,128,128,0.18))' : 'var(--brand)', color: state === 'busy' ? 'var(--muted)' : '#fff' }}>
            {state === 'busy' ? 'Building…' : 'Build lookalike'}
          </button>
          {state === 'error' && <span style={{ fontSize: 12, color: '#e0414a' }}>{err}</span>}
        </div>
      )}
    </div>
  );
}

// Campaign confirm card — drafts an email/SMS campaign to a cohort. Creates a DRAFT
// only; the human approves + SENDS in Engage. Shows the audience reach + the drafted
// copy for review. PII-safe (count + reach only, never people).
const CHAN_TEXT = { email: 'Email', sms: 'SMS', both: 'Email + SMS' };
function CampaignActionCard({ action, suiteId }) {
  const [state, setState] = useState('');
  const [err, setErr] = useState('');
  const [html, setHtml] = useState(''); // optional uploaded custom HTML body
  const [htmlName, setHtmlName] = useState('');
  const [url, setUrl] = useState('');
  const htmlRef = useRef(null);
  const reach = action.reach || null;
  const reachLine = reach ? `${fmtVal(reach.total)} people${reach.email != null ? ` · ${fmtVal(reach.email)} emailable` : ''}${reach.sms ? ` · ${fmtVal(reach.sms)} SMS` : ''}` : null;
  const onPickHtml = async (e) => {
    const f = e.target.files && e.target.files[0]; if (e.target) e.target.value = '';
    if (!f) return;
    try { const t = await f.text(); setHtml(t.slice(0, 500000)); setHtmlName(f.name); } catch { /* ignore */ }
  };
  const create = async () => {
    setState('busy'); setErr('');
    try {
      const r = await api.owlDraftCampaign({ entityId: action.entityId, name: action.name, channel: action.channel, goal: action.goal, audience: action.audience, audienceName: action.summary, subject: action.subject, preheader: action.preheader, body: action.body, ctaText: action.ctaText, ctaUrl: action.ctaUrl, customHtml: html || undefined, contentMode: action.contentMode, blocks: action.blocks, theme: action.theme, suiteId: suiteId || undefined, master: action.master || undefined, dedupeAgainst: action.dedupeAgainst || undefined, dedupeMaster: action.dedupeMaster || undefined });
      setUrl((r && r.url) || '');
      setState('done');
    } catch (e) { setState('error'); setErr((e && e.message) || 'Could not create the campaign.'); }
  };
  return (
    <div style={{ margin: '2px 0 10px', border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '10px 12px', maxWidth: '85%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
        <span style={{ fontSize: 15 }}>📣</span>
        <strong style={{ fontSize: 12.5 }}>Campaign</strong>
        <span style={{ fontSize: 11, color: 'var(--muted)', border: '1px solid var(--hairline)', borderRadius: 980, padding: '1px 7px' }}>Draft</span>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{CHAN_TEXT[action.channel] || 'Email'}</span>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 6 }}>To <strong style={{ color: 'var(--text)' }}>{action.summary || 'the cohort'}</strong>{reachLine ? ` · ${reachLine}` : ''}.</div>
      {/* A linked send gives up whoever the campaigns it goes out with already
          reached — say so before the tap, not after the numbers look wrong. */}
      {(action.dedupeMaster || (action.dedupeAgainst || []).length > 0) && (
        <div style={{ fontSize: 11.5, color: '#b45309', marginBottom: 6 }}>Leaves out anyone already reached by {action.dedupeMaster ? `the rest of “${action.master}”` : `${action.dedupeAgainst.length} linked campaign${action.dedupeAgainst.length === 1 ? '' : 's'}`} — the exact overlap shows on the draft in Campaigns.</div>
      )}
      {action.subject && <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 3 }}><span style={{ color: 'var(--muted)' }}>Subject:</span> <strong>{action.subject}</strong></div>}
      {action.preheader && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 3 }}>Preview text: {action.preheader}</div>}
      {action.body && <div style={{ fontSize: 12.5, color: 'var(--text)', whiteSpace: 'pre-wrap', maxHeight: 150, overflow: 'auto', border: '1px solid var(--hairline)', borderRadius: 8, padding: '6px 8px', margin: '4px 0 8px', background: 'var(--bg, var(--card))' }}>{action.body}</div>}
      {state !== 'done' && (
        <div style={{ marginBottom: 8 }}>
          {!html ? (
            <button onClick={() => htmlRef.current && htmlRef.current.click()} style={{ ...msgActionStyle, fontSize: 12 }}>⬆ Upload custom HTML (optional)</button>
          ) : (
            <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>📄 {htmlName} attached — used instead of the templated body. <button onClick={() => { setHtml(''); setHtmlName(''); }} style={{ ...msgActionStyle, fontSize: 11.5, color: 'var(--brand)' }}>remove</button></span>
          )}
          <input ref={htmlRef} type="file" accept=".html,.htm,text/html" onChange={onPickHtml} style={{ display: 'none' }} />
        </div>
      )}
      {state === 'done' ? (
        <div style={{ fontSize: 12.5, color: 'var(--brand)', fontWeight: 600 }}>✓ Draft created — review, approve &amp; send it in Engage → Campaigns. Nothing has been sent.<ViewLink url={url} label="View campaigns →" /></div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={create} disabled={state === 'busy'}
            style={{ border: 'none', borderRadius: 980, padding: '6px 16px', fontSize: 12.5, fontWeight: 700, cursor: state === 'busy' ? 'default' : 'pointer', background: state === 'busy' ? 'var(--elevated, rgba(128,128,128,0.18))' : 'var(--brand)', color: state === 'busy' ? 'var(--muted)' : '#fff' }}>
            {state === 'busy' ? 'Creating…' : 'Create draft campaign'}
          </button>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>You approve &amp; send in Engage</span>
          {state === 'error' && <span style={{ fontSize: 12, color: '#e0414a' }}>{err}</span>}
        </div>
      )}
    </div>
  );
}

// One-tap "Report to Claude": copies the fix brief to the clipboard (falls back to the
// share sheet on devices without clipboard access) so it can be pasted straight to Claude.
function ReportToClaude({ question, answer, sources, scopeLabel, dashboardId }) {
  const [state, setState] = useState(''); // '' | 'copied' | 'failed'
  const brief = () => buildFixBrief({ question, answer, sources, scopeLabel, dashboardId });
  const onClick = async () => {
    const text = brief();
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) { await navigator.clipboard.writeText(text); setState('copied'); }
      else if (navigator.share) { await navigator.share({ title: 'Owl fix brief', text }); setState('copied'); }
      else throw new Error('no clipboard');
    } catch { setState('failed'); }
    setTimeout(() => setState(''), 2500);
  };
  return (
    <button
      onClick={onClick}
      title="Copy a fix brief (question + answer + the exact query behind it) to paste to Claude"
      style={{ marginTop: 4, border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer', fontSize: 11.5, padding: '2px 4px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      🛠 {state === 'copied' ? 'Copied — paste to Claude' : state === 'failed' ? 'Copy failed — long-press to select' : 'Report to Claude'}
    </button>
  );
}

// An auto-chart with a type toggle (bar / line / pie / metric) + a 📌 pin button.
// Switching type is instant + client-side; pinning saves the live query as a tile.
function OwlChart({ source, entityId, suiteId, canPin }) {
  const [type, setType] = useState(source.chartType || 'bar');
  const [pinOpen, setPinOpen] = useState(false);
  const [stacked, setStacked] = useState(false);
  const [followMsg, setFollowMsg] = useState('');
  // Follow this chart in the briefing: materialise it as a tile (on the home "Saved
  // from Owl" board) and add a 'follow' mark so the home briefing always addresses it.
  const doFollow = async () => {
    setFollowMsg('…');
    try {
      await api.owlPin({ entityId, suiteId: suiteId || undefined, target: 'home', follow: true, title: `${source.measure}${source.dimensions && source.dimensions.length ? ` by ${source.dimensions.join(', ')}` : ''}`, queryBody: source.queryBody, chartType: type });
      setFollowMsg('👁 Following — your briefing will cover this');
    } catch (e) { setFollowMsg(`⚠ ${(e && e.message) || 'Could not follow.'}`); }
    setTimeout(() => setFollowMsg(''), 2800);
  };
  const measCols = source.columns.filter((c) => c.kind === 'measure');
  const meas = measCols[0];
  const multiMeasure = measCols.length >= 2;
  const dims = source.columns.filter((c) => c.kind === 'dimension');
  const rowCount = (source.rows || []).length;
  const canPie = !multiMeasure && dims.length === 1 && rowCount >= 2 && rowCount <= 12;
  const opts = [{ k: 'bar', label: 'Bar' }, { k: 'line', label: 'Line' }, ...(canPie ? [{ k: 'pie', label: 'Pie' }] : []), { k: 'metric', label: 'Metric' }, { k: 'table', label: 'Table' }];
  const seg = (active) => ({ padding: '3px 9px', fontSize: 11, fontWeight: 600, border: 'none', borderRadius: 980, cursor: 'pointer', background: active ? 'var(--brand)' : 'transparent', color: active ? '#fff' : 'var(--text)' });
  const total = (source.rows || []).reduce((a, r) => a + (Number(r[meas?.field]) || 0), 0);
  const showPin = canPin && source.queryBody && entityId;
  // Stack applies to any multi-series chart: several measures, OR a two-dimension
  // result pivoted into one series per category (e.g. check-ins by gate over hours).
  const canStack = (multiMeasure || !!pivotInfo(source)) && (type === 'bar' || type === 'line');
  return (
    <div style={{ margin: '2px 0 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <div style={{ display: 'inline-flex', gap: 2, padding: 2, background: 'var(--elevated, rgba(128,128,128,0.12))', borderRadius: 980 }}>
          {opts.map((o) => <button key={o.k} onClick={() => setType(o.k)} style={seg(type === o.k)}>{o.label}</button>)}
        </div>
        {canStack && <button onClick={() => setStacked((s) => !s)} title="Stack the series" style={{ border: '1px solid var(--hairline)', background: stacked ? 'var(--brand)' : 'var(--card)', color: stacked ? '#fff' : 'var(--text)', borderRadius: 980, padding: '3px 10px', fontSize: 11.5, cursor: 'pointer' }}>{stacked ? 'Stacked' : 'Stack'}</button>}
        {showPin && <button onClick={() => setPinOpen((o) => !o)} title="Pin to a dashboard or home" style={{ border: '1px solid var(--hairline)', background: pinOpen ? 'var(--elevated, rgba(128,128,128,0.12))' : 'var(--card)', borderRadius: 980, padding: '3px 10px', fontSize: 11.5, cursor: 'pointer', color: 'var(--text)' }}>📌 Pin</button>}
        {showPin && <button onClick={doFollow} title="Follow in your briefing — the home briefing will always read & address this" style={{ border: '1px solid var(--hairline)', background: 'var(--card)', borderRadius: 980, padding: '3px 10px', fontSize: 11.5, cursor: 'pointer', color: 'var(--text)' }}>👁 Follow</button>}
        {followMsg && <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{followMsg}</span>}
      </div>
      {showPin && pinOpen && <PinMenu source={source} entityId={entityId} suiteId={suiteId} chartType={type} onDone={() => setPinOpen(false)} />}
      {type === 'metric'
        ? (
          <div style={{ border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', padding: '18px 14px', textAlign: 'center' }}>
            <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--text)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{fmtVal(total)}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{meas?.label} · total</div>
          </div>
        )
        : type === 'table'
          ? <SourceTable source={source} />
          : (
          <div style={{ height: 200, border: '1px solid var(--hairline)', borderRadius: 12, overflow: 'hidden', background: 'var(--card)' }}>
            <ChartTile data={chartDataFromSource({ ...source, chartType: type })} visConfig={{ type: VIS[type] || 'looker_column', stacking: (canStack && stacked) ? 'normal' : undefined }} />
          </div>
        )}
    </div>
  );
}

// The pin dialog: name it, choose Home or a dashboard, save it as a live tile.
function PinMenu({ source, entityId, suiteId, chartType, onDone }) {
  const defaultTitle = `${source.measure}${source.dimensions && source.dimensions.length ? ' by ' + source.dimensions.join(', ') : ''}`;
  const [title, setTitle] = useState(defaultTitle);
  const [target, setTarget] = useState('home');
  const [dashboards, setDashboards] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [follow, setFollow] = useState(false);
  useEffect(() => { let on = true; api.owlPinTargets(entityId).then((r) => { if (on) setDashboards(r.dashboards || []); }).catch(() => {}); return () => { on = false; }; }, [entityId]);
  async function pin() {
    setBusy(true);
    try {
      const r = await api.owlPin({ entityId, suiteId: suiteId || undefined, target, title, queryBody: source.queryBody, chartType, follow });
      setDone(`Pinned to ${target === 'home' ? 'Home' : (r.dashboardTitle || 'the dashboard')}${follow ? ' · 👁 following in your briefing' : ''} ✓`);
    } catch (e) { setDone(`⚠ ${(e && e.message) || 'Could not pin.'}`); }
    setBusy(false);
  }
  const fld = { width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--text)', fontSize: 13, marginTop: 6 };
  if (done) return <div style={{ border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--bg, #fafafe)', padding: '10px 12px', fontSize: 13, marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center' }}><span>{done}</span><button onClick={onDone} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: 'var(--muted)', cursor: 'pointer' }}>✕</button></div>;
  return (
    <div style={{ border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--bg, #fafafe)', padding: '10px 12px', marginBottom: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--muted)' }}>📌 Pin chart</div>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" style={fld} />
      <select value={target} onChange={(e) => setTarget(e.target.value)} style={fld}>
        <option value="home">🏠 Home page</option>
        {(() => {
          const groups = {};
          for (const d of dashboards) { const f = d.folder || ''; (groups[f] = groups[f] || []).push(d); }
          const folders = Object.keys(groups).sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)));
          return folders.map((f) => (f === ''
            ? groups[f].map((d) => <option key={d.id} value={d.id}>{d.title}</option>)
            : <optgroup key={f} label={f}>{groups[f].map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}</optgroup>));
        })()}
      </select>
      <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 9, fontSize: 12.5, color: 'var(--text)', cursor: 'pointer' }}>
        <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} />
        <span>👁 Follow in my briefing <span style={{ color: 'var(--muted)' }}>— the home briefing will read &amp; address it</span></span>
      </label>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button onClick={pin} disabled={busy || !title.trim()} style={{ border: 'none', borderRadius: 8, padding: '7px 14px', fontWeight: 700, fontSize: 13, cursor: busy ? 'default' : 'pointer', background: 'var(--brand)', color: '#fff' }}>{busy ? 'Pinning…' : 'Pin'}</button>
        <button onClick={onDone} style={{ border: '1px solid var(--hairline)', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', background: 'var(--card)', color: 'var(--text)' }}>Cancel</button>
      </div>
    </div>
  );
}

// Citation chips — the grounding made visible. One "source" per live askData call
// in an answer: a green dot (= real query, not invented), the measure + value, the
// filters/scope, and a tap-to-expand card with the exact query.
function CitationChips({ sources, entityId, suiteId, canPin }) {
  const [open, setOpen] = useState(false);
  // Only data sources (askData/queryDashboard) render as chips/charts; dashboard-read
  // sources carry query detail for the fix-brief but have no chart/table to show here.
  const dataSources = (sources || []).filter((s) => s.kind !== 'dashboard');
  const chip = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 9px', borderRadius: 980, background: 'var(--card)', border: '1px solid var(--hairline)', fontSize: 11.5, color: '#3a3a3c', cursor: 'default' };
  const dot = { width: 7, height: 7, borderRadius: '50%', background: '#34c759', flex: 'none' };
  const muted = { color: 'var(--muted)' };
  if (!dataSources.length) return null;
  return (
    <div style={{ margin: '-4px 0 12px 2px' }}>
      {/* Charts stay visible; the query/source detail tucks into one "Beneath the hood" dropdown. */}
      {dataSources.map((s, i) => (s.chartType && s.rows && s.rows.length > 1
        ? <OwlChart key={i} source={s} entityId={entityId} suiteId={suiteId} canPin={canPin} /> : null))}
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--hairline)', background: 'var(--card)', color: 'var(--muted)', borderRadius: 980, padding: '4px 11px', fontSize: 11.5, cursor: 'pointer', marginTop: 2 }}>
        <span style={dot} /> Beneath the hood <span style={{ fontSize: 10 }}>{open ? '▴' : '▾'}</span>
      </button>
      {open && dataSources.map((s, i) => (
        <div key={i} style={{ marginTop: 8, border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--bg, #fafafe)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', padding: '9px 11px' }}>
            <span style={{ ...chip, cursor: 'default' }}>
              <b style={{ fontWeight: 650, color: 'var(--text)' }}>{s.measure}</b>
              {s.value != null
                ? <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtVal(s.value)}</span>
                : <span style={muted}>{s.count} rows</span>}
            </span>
            {(s.filters || []).map((f, j) => (<span key={j} style={chip}><span style={muted}>{f.label}</span> {f.value}</span>))}
            {s.explore && <span style={chip}><span style={muted}>explore</span> {s.explore} · live</span>}
          </div>
          {s.columns && s.rows && s.rows.length > 0 && (
            <div style={{ overflow: 'auto', maxHeight: 240, borderTop: '1px solid var(--hairline)' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11.5 }}>
                <thead>
                  <tr>{s.columns.map((c, k) => <th key={k} style={{ textAlign: 'left', padding: '6px 10px', position: 'sticky', top: 0, background: 'var(--elevated, #f1f1f5)', color: 'var(--muted)', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: '1px solid var(--hairline)' }}>{c.label}</th>)}</tr>
                </thead>
                <tbody>
                  {s.rows.map((r, ri) => (
                    <tr key={ri}>{s.columns.map((c, k) => <td key={k} style={{ padding: '5px 10px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--hairline)', fontVariantNumeric: 'tabular-nums', color: 'var(--text)' }}>{fmtCell(r[c.field])}</td>)}</tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: 'flex', alignItems: 'center', padding: '6px 10px', fontSize: 10.5, color: 'var(--muted)' }}>
                {s.count > s.rows.length ? `Showing ${s.rows.length} of ${s.count.toLocaleString()} rows.` : ''}
                <button onClick={() => downloadText(csvName(s), toCSV(s.columns, s.rows))} title="Download as CSV" style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: 'var(--brand)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>⬇ CSV</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
